import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CONFIG, { getUserCity, isServiceEnabled, fetchRemoteConfig, getServiceFees } from "../../config";
import GoogleMapPreview from "../common/GoogleMapPreview";
import CurrentLocationDetector from "../common/CurrentLocationDetector";
import CurrentLocationCard from "../common/CurrentLocationCard";

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const STORAGE_KEY  = "cartItems";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

// ─────────────────────────────────────────────────────────────────────────────
// AUTH
// ─────────────────────────────────────────────────────────────────────────────
const getLoggedInUser = () => {
  try { return JSON.parse(localStorage.getItem("currentUser") || "null"); }
  catch { return null; }
};

const getAuthToken = () =>
  localStorage.getItem("authToken")    ||
  localStorage.getItem("accessToken")  ||
  localStorage.getItem("access_token") ||
  localStorage.getItem("token") || "";

const authHeaders = () => ({
  "Content-Type": "application/json",
  ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}),
});

// ─────────────────────────────────────────────────────────────────────────────
// CART NORMALISER  — the single source of truth for item shape
// Every field SummaryPage uses must be present after normalisation.
// This is what fixes the blank page: previously items arrived with missing
// fields (e.g. no `name`, no `price`) causing renders to silently bail.
// ─────────────────────────────────────────────────────────────────────────────
const normalizeItem = (item, index) => {
  if (!item || typeof item !== "object") return null;
  const id = item.id ?? item.service_id ?? `item-${index}`;
  if (!id) return null;
  return {
    id:            String(id),
    dbId:          item.dbId || String(item._id || id),
    title:         item.title    || item.service_name || item.name || "Service",
    name:          item.name     || item.title        || item.service_name || "Service",
    short:         item.short    || item.subtitle     || item.category || "",
    subtitle:      item.subtitle || item.short        || item.category || "",
    price:         Number(item.price  || item.amount  || 0),
    image:         item.image    || null,
    category:      item.category || "",
    qty:           Math.max(1, Number(item.qty || 1)),
    bookingDate:   item.bookingDate   || "",
    bookingTime:   item.bookingTime   || "",
    bookingSlotId: item.bookingSlotId || "",
    // Schedule Booking wizard markers — passed through untouched so
    // place_order() can record the booking in the right per-category
    // collection (schedule_home_service / _technical_service / _outdoor_service).
    schedule_booking:      item.schedule_booking      || false,
    schedule_duration:     item.schedule_duration     || null,
    schedule_category_key: item.schedule_category_key || "",
  };
};

const normalizeCart = (raw) => {
  if (!Array.isArray(raw)) return [];
  // normalise → remove nulls → deduplicate by id (keep highest qty)
  const map = new Map();
  raw.forEach((item, i) => {
    const n = normalizeItem(item, i);
    if (!n) return;
    if (map.has(n.id)) {
      const ex = map.get(n.id);
      map.set(n.id, { ...ex, qty: ex.qty + n.qty });
    } else {
      map.set(n.id, n);
    }
  });
  return Array.from(map.values());
};

// ─────────────────────────────────────────────────────────────────────────────
// LOCAL STORAGE HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const readLocalCart = () => {
  try { return normalizeCart(JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]")); }
  catch { return []; }
};

const writeLocalCart = (items) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  window.dispatchEvent(new Event("cart-updated"));
};

const clearLocalCart = () => {
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new Event("cart-updated"));
};

// ─────────────────────────────────────────────────────────────────────────────
// RESOLVE CART  — the critical fix for the blank page
//
// Priority order:
//   1. navigation state (passed by ProfileDashboard)
//   2. localStorage  (fallback — direct URL access / refresh)
//
// We validate that each item has at minimum an id and a price > 0.
// If state cartItems is present but empty/invalid we fall back to localStorage
// so a browser refresh never shows a blank page.
// ─────────────────────────────────────────────────────────────────────────────
const resolveCart = (locationState) => {
  // Try navigation state first
  if (Array.isArray(locationState?.cartItems) && locationState.cartItems.length > 0) {
    const fromState = normalizeCart(locationState.cartItems);
    if (fromState.length > 0) return fromState;
  }
  // Fall back to localStorage
  return readLocalCart();
};

// ─────────────────────────────────────────────────────────────────────────────
// BACKEND CART API
// ─────────────────────────────────────────────────────────────────────────────
const apiCartAdd = async (mobile, items) => {
  if (!mobile || !items?.length) return;
  try {
    await fetch(`${API_BASE_URL}/api/user/user-cart/${encodeURIComponent(mobile)}/add/`, {
      method:"POST", credentials:"include", headers:authHeaders(),
      body:JSON.stringify({ items }),
    });
  } catch (e) { console.warn("[Cart] add error:", e.message); }
};

const apiCartRemoveItem = async (mobile, item) => {
  if (!mobile || !item) return;
  try {
    const url = item.dbId
      ? `${API_BASE_URL}/api/user/user-cart/${encodeURIComponent(mobile)}/remove/${encodeURIComponent(item.dbId)}/`
      : `${API_BASE_URL}/api/user/user-cart/${encodeURIComponent(mobile)}/remove-by-service/${encodeURIComponent(String(item.id))}/`;
    await fetch(url, { method:"DELETE", credentials:"include", headers:authHeaders() });
  } catch (e) { console.warn("[Cart] remove error:", e.message); }
};

const apiCartClear = async (mobile) => {
  if (!mobile) return;
  try {
    await fetch(`${API_BASE_URL}/api/user/user-cart/${encodeURIComponent(mobile)}/clear/`, {
      method:"DELETE", credentials:"include", headers:authHeaders(),
    });
  } catch (e) { console.warn("[Cart] clear error:", e.message); }
};

const apiPlaceOrder = async (payload) => {
  const res  = await fetch(`${API_BASE_URL}/api/user/place-order/`, {
    method:"POST", credentials:"include", headers:authHeaders(), body:JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok || !data?.success) {
    const err = new Error(data?.message || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return data;
};

// ─────────────────────────────────────────────────────────────────────────────
// SLOT AVAILABILITY
// ─────────────────────────────────────────────────────────────────────────────
const fetchSlotAvailability = async (serviceId) => {
  const res  = await fetch(`${API_BASE_URL}/api/booking/slot-availability`, {
    method:"POST", headers:{ "Content-Type":"application/json" },
    body:JSON.stringify({ serviceId:String(serviceId) }),
  });
  const data = await res.json();
  if (!res.ok || !data?.success) throw new Error(data?.message || "Unable to load slot availability.");
  return { success:true, serviceId, days:Array.isArray(data?.days) ? data.days : [] };
};

const isSlotSelectable = (slot) =>
  Boolean(slot?.availability) && Number(slot?.seats||0) - Number(slot?.booked||0) > 0;

// ─────────────────────────────────────────────────────────────────────────────
// MISC
// ─────────────────────────────────────────────────────────────────────────────
const ADDR_LABEL_ICONS = { Home: "🏠", Work: "🏢", Other: "📍" };

// ─────────────────────────────────────────────────────────────────────────────
// CURRENT LOCATION (Google Maps)
// GOOGLE_MAPS_API_KEY lives in src/config.js. While it's blank, "Current
// Location" runs in Testing Mode — pick one of a few dummy geo locations
// instead of hitting a real API. As soon as a real key is pasted into
// config.js, this automatically switches to real GPS + Google reverse
// geocoding — no other code change needed.
// ─────────────────────────────────────────────────────────────────────────────
const GMAPS_KEY           = CONFIG?.GOOGLE_MAPS_API_KEY || "";
const IS_GOOGLE_CONFIGURED = Boolean(GMAPS_KEY);

const DUMMY_TEST_LOCATIONS = [
  { id: "test-1", label: "Current Location", line1: "MG Road",             area: "MG Road",             city: "Bengaluru", state: "Karnataka", pincode: "560001", lat: 12.9757, lng: 77.6069 },
  { id: "test-2", label: "Current Location", line1: "Devaraja Mohalla",    area: "Devaraja Mohalla",    city: "Mysuru",     state: "Karnataka", pincode: "570001", lat: 12.3052, lng: 76.6552 },
  { id: "test-3", label: "Current Location", line1: "Whitefield",         area: "Whitefield",           city: "Bengaluru", state: "Karnataka", pincode: "560066", lat: 12.9698, lng: 77.7500 },
];

const fmt = (v) => `₹${Number(v||0).toLocaleString("en-IN")}`;

// Shopping cart items are marked with category:"Shopping — <category>" in
// ShoppingPage.js's handleAdd() — used to skip the professional-visit time
// slot picker (they're delivered, not scheduled) and just ask for a date.
const isShoppingItem = (item) => String(item?.category || "").startsWith("Shopping");

const formatSlotLabel = (item) =>
  item?.bookingDate && item?.bookingTime
    ? `${item.bookingDate} · ${item.bookingTime}`
    : "Select booking slot";

const formatBackendDateLabel = (date) => {
  if (!date) return "";
  const [dd,mm,yyyy] = String(date).split("-");
  return `${dd}-${mm}-${yyyy}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// STEP PROGRESS
// ─────────────────────────────────────────────────────────────────────────────
const StepProgress = () => {
  const steps = [
    { title:"Services", sub:"Selected" },
    { title:"Schedule", sub:"Per item" },
    { title:"Confirm",  sub:"Current"  },
  ];
  return (
    <div style={S.stepWrap}>
      {steps.map((step, i) => {
        const active    = i === 2;
        const completed = i < 2;
        return (
          <div key={step.title} style={{ ...S.stepItem, ...(active?S.stepItemActive:{}) }}>
            <div style={{ ...S.stepCircle, ...(completed?S.stepCircleDone:{}), ...(active?S.stepCircleActive:{}) }}>
              {completed ? "✓" : i+1}
            </div>
            <div style={S.stepInfo}>
              <div style={{ ...S.stepTitle, color:active?"#312e81":"#0f172a" }}>{step.title}</div>
              <div style={S.stepSub}>{step.sub}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SLOT MODAL
// ─────────────────────────────────────────────────────────────────────────────
const SlotModal = ({
  open, item, availabilityDays, loadingAvailability, availabilityError,
  selectedAvailabilityDate, onSelectDate, draftDate, draftTime,
  onSelectTime, onClose, onSave,
}) => {
  if (!open || !item) return null;
  const activeDay   = availabilityDays.find((d) => d.date === selectedAvailabilityDate) || availabilityDays[0];
  const previewDay  = availabilityDays.find((d) => d.date === draftDate);
  const previewSlot = previewDay?.slots?.find((s) => s.time === draftTime);

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
        <div style={S.modalHead}>
          <div>
            <h3 style={S.modalTitle}>Select booking slot</h3>
            <p style={S.modalDesc}>Choose a schedule for <strong>{item.name}</strong>.</p>
          </div>
          <button style={S.modalCloseBtn} onClick={onClose}>✕</button>
        </div>

        <div style={S.modalBody}>
          <div style={S.modalServiceInfo}>
            {item.image
              ? <img src={item.image} alt={item.name} style={S.modalServiceImg} />
              : <div style={S.modalServiceFallback}>{item.name?.charAt(0)||"S"}</div>
            }
            <div>
              <div style={S.modalServiceName}>{item.name}</div>
              <div style={S.modalServiceSub}>Qty: {item.qty||1} · Price: {fmt(item.price)}</div>
              <div style={S.modalServiceHint}>Slots marked full or unavailable cannot be selected.</div>
            </div>
          </div>

          {loadingAvailability ? (
            <div style={S.availabilityStateBox}>Loading slot availability…</div>
          ) : availabilityError ? (
            <div style={S.availabilityErrorBox}>{availabilityError}</div>
          ) : (
            <>
              <div style={S.slotSection}>
                <label style={S.label}>Available Dates</label>
                <div style={S.dateGrid}>
                  {availabilityDays.map((day) => {
                    const sel     = selectedAvailabilityDate === day.date;
                    const hasOpen = day.slots.some(isSlotSelectable);
                    return (
                      <button key={day.date} type="button" onClick={() => onSelectDate(day.date)}
                        style={{ ...S.dateBtn, ...(sel?S.dateBtnActive:{}), ...(!hasOpen?S.dateBtnFull:{}) }}>
                        <span style={S.dateBtnTop}>{formatBackendDateLabel(day.date)}</span>
                        <span style={S.dateBtnBottom}>{day.slots.filter(isSlotSelectable).length}/{day.slots.length} open</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={S.slotSection}>
                <label style={S.label}>Available Time Slots</label>
                <div style={S.slotGridResponsive}>
                  {(activeDay?.slots||[]).map((slot) => {
                    const selectable = isSlotSelectable(slot);
                    const freeSeats  = Number(slot.seats||0) - Number(slot.booked||0);
                    const selected   = draftDate === activeDay?.date && draftTime === slot.time;
                    return (
                      <button key={`${activeDay?.date}-${slot.time}`} type="button"
                        disabled={!selectable}
                        onClick={() => selectable && onSelectTime(activeDay.date, slot.time, slot._id||"")}
                        style={{ ...S.slotBtn, ...(selected?S.slotBtnActive:{}), ...(!selectable?S.slotBtnDisabled:{}) }}>
                        <div style={S.slotBtnTime}>{slot.time}</div>
                        <div style={S.slotBtnMeta}>
                          {selectable
                            ? <span style={S.seatBadge}>{freeSeats} seat{freeSeats!==1?"s":""} left</span>
                            : <span style={S.seatBadgeFull}>{!slot.availability?"Unavailable":"Fully booked"}</span>
                          }
                        </div>
                        <div style={S.capacityBarWrap}>
                          <div style={{ ...S.capacityBar,
                            width:`${Number(slot.seats||0)>0?Math.min(100,(Number(slot.booked||0)/Number(slot.seats||0))*100):0}%`,
                            background:selectable?(freeSeats<=1?"#f59e0b":"#4f46e5"):"#ef4444" }} />
                        </div>
                        <div style={S.slotCapacityText}>{slot.booked}/{slot.seats} booked</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {draftDate && draftTime && previewSlot && (
                <div style={S.backendSampleCard}>
                  <div style={S.backendSampleTitle}>Selected slot details</div>
                  <div style={S.backendSampleBody}>
                    Slot ID: {previewSlot._id||"N/A"}<br/>
                    Date: {draftDate} · Time: {draftTime}<br/>
                    Free seats: {Number(previewSlot.seats||0)-Number(previewSlot.booked||0)}
                  </div>
                </div>
              )}
            </>
          )}

          <div style={S.selectedSlotPreview}>
            <span style={S.selectedSlotLabel}>Selected slot</span>
            <span style={S.selectedSlotValue}>
              {draftDate && draftTime ? `${draftDate} · ${draftTime}` : "Please choose a date and time"}
            </span>
          </div>
        </div>

        <div style={S.modalFooter}>
          <button style={S.secondaryBtn} onClick={onClose}>Cancel</button>
          <button style={{ ...S.primaryBtn, ...(!(draftDate&&draftTime)?S.disabledBtn:{}) }}
            disabled={!(draftDate&&draftTime)} onClick={onSave}>
            Save Slot
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SUMMARY PAGE
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// PAYMENT PAGE — handles two cases:
//  - Rendered when UserDashboard navigates here with { mode:"pay_order", existingOrder }
//    → pay-after-work remaining balance + optional tip. Calls process_order_payment
//      with stage "after_work" and returns to dashboard.
//  - Rendered when ConfirmDonePage redirects here with { mode:"pay_order",
//    existingOrder, tipOnly:true, initialTip } → the service is already fully
//    paid (pay_now order), this screen ONLY collects the tip the user picked
//    on the wrap-up screen (still changeable here), then closes the job with
//    the same user-confirm call ConfirmDonePage used to make inline.
// ─────────────────────────────────────────────────────────────────────────────
const PayOrderPage = ({ order, userMobile, navigate, tipOnly = false, initialTip = 0 }) => {
  const [paying,  setPaying]  = useState(false);
  const [error,   setError]   = useState("");
  const [success, setSuccess] = useState(false);

  const docId       = order?._id || order?.order_id;
  const baseAmount  = tipOnly ? 0 : Number(order?.total_amount || 0);
  const serviceName = order?.service || order?.service_name || "Service Booking";
  const orderId     = String(order?.order_id || order?._id || "").slice(-8).toUpperCase();
  const fmt         = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

  // ── Optional tip — added here since the user has now seen the finished job ──
  const TIP_CONFIG   = CONFIG.PARTNER_TIP ?? { MIN_AMOUNT: 10, PRESETS: [10, 20, 50, 100] };
  const [tipAmount,     setTipAmount]     = useState(initialTip);
  const [customTipOpen, setCustomTipOpen] = useState(false);
  const [customTipText, setCustomTipText] = useState("");
  const amount = baseAmount + tipAmount;

  const handleSelectTipPreset = (amt) => {
    setCustomTipOpen(false); setCustomTipText("");
    setTipAmount((prev) => (prev === amt ? 0 : amt));
  };
  const handleRemoveTip = () => { setTipAmount(0); setCustomTipOpen(false); setCustomTipText(""); };
  const handleCustomTipApply = () => {
    const n = Math.floor(Number(customTipText) || 0);
    if (n >= TIP_CONFIG.MIN_AMOUNT) { setTipAmount(n); setCustomTipOpen(false); }
  };

  const handlePay = async () => {
    if (paying || !docId) return;
    setPaying(true); setError("");
    try {
      // Tip-only + no tip left (user removed it here) → nothing to charge,
      // just close out the job like ConfirmDonePage's "Confirm Job Done" did.
      if (!(tipOnly && amount === 0)) {
        const res = await fetch(`${API_BASE_URL}/api/orders/scheduled/${encodeURIComponent(docId)}/pay/`, {
          method:  "POST",
          headers: authHeaders(),
          body:    JSON.stringify({
            payment_stage: tipOnly ? "tip_only" : "after_work",
            mobile: userMobile,
            tip_amount: tipAmount,
          }),
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d?.error || d?.message || `Error ${res.status}`);
        }
      }
      // Tip-only orders were already fully paid for the service itself —
      // this screen only collects the tip, so the job still needs the same
      // formal-close call ConfirmDonePage used to make inline before this
      // redirect existed.
      if (tipOnly) {
        const confirmRes = await fetch(`${API_BASE_URL}/api/orders/${encodeURIComponent(docId)}/user-confirm/`, {
          method:  "POST",
          headers: authHeaders(),
          body:    JSON.stringify({ mobile: userMobile }),
        });
        if (!confirmRes.ok) {
          const d = await confirmRes.json().catch(() => ({}));
          throw new Error(d?.error || d?.message || `Error ${confirmRes.status}`);
        }
      }
      setSuccess(true);
      // A tip was added → skip the bookings dashboard, go straight home.
      if (tipAmount > 0) {
        setTimeout(() => navigate("/", { replace: true, state: { tipThanks: true } }), 1800);
      } else {
        setTimeout(() => navigate("/profile-dashboard", { replace: true, state: { paymentSuccess: true, orderId } }), 1800);
      }
    } catch (e) {
      setError(e.message || "Payment failed. Please try again.");
    } finally {
      setPaying(false);
    }
  };

  if (success) {
    return (
      <div style={{ ...PS.page, display:"flex", alignItems:"center", justifyContent:"center" }}>
        <div style={PS.card}>
          <div style={{ fontSize:56, textAlign:"center" }}>✅</div>
          <h2 style={{ textAlign:"center", fontWeight:900, fontSize:24, margin:"14px 0 8px" }}>
            {tipOnly && amount === 0 ? "Job Confirmed!" : "Payment Successful!"}
          </h2>
          <p style={{ textAlign:"center", color:"#6b7280", fontSize:14 }}>
            {tipAmount > 0 ? "Thanks for the tip! Redirecting home…" : "Returning to your orders…"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={PS.page}>
      <div style={PS.container}>
        <button style={PS.backBtn} onClick={() => navigate(-1)}>← Back</button>

        <div style={PS.header}>
          <p style={PS.eyebrow}>{tipOnly ? "Job Completed" : "Payment"}</p>
          <h1 style={PS.title}>{tipOnly ? "Confirm & Add Tip" : "Complete Your Payment"}</h1>
          <p style={PS.sub}>
            {tipOnly
              ? "Your partner marked this job as done. Add a tip if you'd like, then confirm."
              : "Your job is done — please pay to close the booking."}
          </p>
        </div>

        <div style={PS.card}>
          {/* Order summary rows */}
          <div style={PS.summarySection}>
            {orderId && (
              <div style={PS.row}>
                <span style={PS.rowLabel}>Order #</span>
                <span style={PS.rowVal}>{orderId}</span>
              </div>
            )}
            <div style={PS.row}>
              <span style={PS.rowLabel}>Service</span>
              <span style={{ ...PS.rowVal, maxWidth:"60%", textAlign:"right" }}>{serviceName}</span>
            </div>
            {tipOnly && (
              <div style={PS.row}>
                <span style={PS.rowLabel}>Payment</span>
                <span style={{ ...PS.rowVal, color:"#16a34a" }}>✓ Already paid</span>
              </div>
            )}
            {!tipOnly && tipAmount > 0 && (
              <div style={PS.row}>
                <span style={PS.rowLabel}>Partner Tip</span>
                <span style={PS.rowVal}>{fmt(tipAmount)}</span>
              </div>
            )}
            {amount > 0 && (
              <div style={{ ...PS.row, borderTop:"2px solid #e5e7eb", paddingTop:14, marginTop:6 }}>
                <span style={{ fontWeight:900, fontSize:16 }}>{tipOnly ? "Tip Amount" : "Total Due"}</span>
                <span style={{ fontWeight:900, fontSize:22, color:"#1d4ed8" }}>{fmt(amount)}</span>
              </div>
            )}
          </div>

          {/* ── Partner tip — how was the service? ─────────────────────── */}
          <div style={PS.tipBox}>
            <div style={PS.tipTitle}>Happy with the work? Add a tip 🙌</div>
            <div style={PS.tipSub}>100% goes to the partner who completed your job. Optional.</div>
            <div style={PS.tipChipRow}>
              {TIP_CONFIG.PRESETS.map((amt) => (
                <button key={amt} type="button"
                  style={{ ...PS.tipChip, ...(tipAmount === amt ? PS.tipChipActive : {}) }}
                  onClick={() => handleSelectTipPreset(amt)}>
                  {fmt(amt)}
                </button>
              ))}
              <button type="button"
                style={{ ...PS.tipChip, ...(customTipOpen ? PS.tipChipActive : {}) }}
                onClick={() => setCustomTipOpen((v) => !v)}>
                Custom
              </button>
              {tipAmount > 0 && (
                <button type="button" style={PS.tipRemoveBtn} onClick={handleRemoveTip}>
                  Remove tip ✕
                </button>
              )}
            </div>
            {customTipOpen && (
              <div style={PS.tipCustomRow}>
                <input
                  type="number"
                  min={TIP_CONFIG.MIN_AMOUNT}
                  placeholder={`Min ${fmt(TIP_CONFIG.MIN_AMOUNT)}`}
                  value={customTipText}
                  onChange={(e) => setCustomTipText(e.target.value)}
                  style={PS.tipCustomInput}
                />
                <button type="button" style={PS.tipCustomApplyBtn} onClick={handleCustomTipApply}>
                  Apply
                </button>
              </div>
            )}
            {customTipOpen && customTipText && Number(customTipText) < TIP_CONFIG.MIN_AMOUNT && (
              <div style={PS.tipMinHint}>Minimum tip is {fmt(TIP_CONFIG.MIN_AMOUNT)}.</div>
            )}
          </div>

          {error && (
            <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:10, padding:"10px 14px", color:"#b91c1c", fontSize:13, marginBottom:16 }}>
              {error}
            </div>
          )}

          <button
            style={{ ...PS.payBtn, opacity: paying ? 0.7 : 1 }}
            disabled={paying}
            onClick={handlePay}
          >
            {paying
              ? "Processing…"
              : tipOnly && amount === 0
                ? "Confirm Job Done"
                : `Pay ${fmt(amount)}`}
          </button>
          <button style={PS.laterBtn} onClick={() => navigate(-1)}>
            {tipOnly ? "Back" : "Pay Later"}
          </button>
        </div>
      </div>
    </div>
  );
};

const PS = {
  page:           { background:"#f1f5f9", minHeight:"100vh", padding:"28px 20px", fontFamily:"Inter,Arial,sans-serif" },
  container:      { maxWidth:480, margin:"0 auto" },
  backBtn:        { background:"none", border:"none", color:"#4f46e5", fontWeight:700, fontSize:14, cursor:"pointer", padding:"0 0 16px", display:"block" },
  header:         { marginBottom:24 },
  eyebrow:        { margin:0, color:"#4f46e5", fontSize:13, fontWeight:800, textTransform:"uppercase", letterSpacing:"0.08em" },
  title:          { margin:"8px 0 0", fontSize:28, fontWeight:900, color:"#0f172a" },
  sub:            { margin:"8px 0 0", fontSize:14, color:"#64748b" },
  card:           { background:"#fff", border:"1px solid #e2e8f0", borderRadius:20, padding:"24px 20px" },
  summarySection: { display:"grid", gap:12, marginBottom:20 },
  row:            { display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:14 },
  rowLabel:       { color:"#6b7280", fontWeight:500 },
  rowVal:         { fontWeight:700, color:"#111827" },
  payBtn:         { width:"100%", padding:"14px 0", borderRadius:14, background:"#1d4ed8", color:"#fff", fontSize:16, fontWeight:800, border:"none", cursor:"pointer", marginBottom:10 },
  laterBtn:       { width:"100%", padding:"12px 0", borderRadius:14, background:"#f3f4f6", color:"#6b7280", fontSize:14, fontWeight:700, border:"1px solid #e5e7eb", cursor:"pointer" },

  // ── Partner tip box ────────────────────────────────────────────────────────
  tipBox:            { background:"#fdf4ff", border:"1px solid #f0abfc", borderRadius:16, padding:"14px 16px", marginBottom:16 },
  tipTitle:          { fontSize:14, fontWeight:800, color:"#701a75" },
  tipSub:            { marginTop:4, fontSize:12, color:"#a21caf" },
  tipChipRow:        { marginTop:12, display:"flex", flexWrap:"wrap", alignItems:"flex-start", gap:8 },
  tipChip:           { flex:"0 0 auto", width:"auto", padding:"8px 16px", borderRadius:999, border:"1.5px solid #e9d5ff", background:"#fff", color:"#701a75", fontWeight:800, fontSize:13, cursor:"pointer", whiteSpace:"nowrap" },
  tipChipActive:     { border:"1.5px solid #a21caf", background:"#a21caf", color:"#fff" },
  tipRemoveBtn:      { flex:"0 0 auto", width:"auto", padding:"8px 14px", borderRadius:999, border:"1.5px solid #fecaca", background:"#fff1f2", color:"#dc2626", fontWeight:700, fontSize:12, cursor:"pointer", whiteSpace:"nowrap" },
  tipCustomRow:      { marginTop:10, display:"flex", gap:8 },
  tipCustomInput:    { flex:"1 1 140px", minWidth:140, height:38, borderRadius:10, border:"1px solid #e9d5ff", padding:"0 12px", fontSize:13, outline:"none", boxSizing:"border-box" },
  tipCustomApplyBtn: { flex:"0 0 auto", width:"auto", height:38, padding:"0 20px", borderRadius:10, border:"none", background:"#a21caf", color:"#fff", fontWeight:800, fontSize:13, cursor:"pointer", whiteSpace:"nowrap" },
  tipMinHint:        { marginTop:6, fontSize:11, color:"#dc2626", fontWeight:600 },
};

// ─────────────────────────────────────────────────────────────────────────────
// CONFIRM JOB DONE PAGE — pay_now orders only (already paid in full).
// Rendered when UserDashboard navigates here with { mode:"confirm_tip", existingOrder }.
// Lets the user optionally add a tip. No tip → closes the job via user-confirm
// directly. Tip selected → redirects to PayOrderPage (mode:"pay_order",
// tipOnly:true) to actually collect the tip payment before confirming, the
// same payment-page flow pay-after-work orders already use for their
// remaining balance.
// ─────────────────────────────────────────────────────────────────────────────
const ConfirmDonePage = ({ order, userMobile, navigate }) => {
  const [confirming, setConfirming] = useState(false);
  const [error,      setError]      = useState("");
  const [success,    setSuccess]    = useState(false);

  const docId       = order?._id || order?.order_id;
  const serviceName = order?.service || order?.service_name || "Service Booking";
  const orderId     = String(order?.order_id || order?._id || "").slice(-8).toUpperCase();
  const fmt         = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

  const TIP_CONFIG   = CONFIG.PARTNER_TIP ?? { MIN_AMOUNT: 10, PRESETS: [10, 20, 50, 100] };
  const [tipAmount,     setTipAmount]     = useState(0);
  const [customTipOpen, setCustomTipOpen] = useState(false);
  const [customTipText, setCustomTipText] = useState("");

  const handleSelectTipPreset = (amt) => {
    setCustomTipOpen(false); setCustomTipText("");
    setTipAmount((prev) => (prev === amt ? 0 : amt));
  };
  const handleRemoveTip = () => { setTipAmount(0); setCustomTipOpen(false); setCustomTipText(""); };
  const handleCustomTipApply = () => {
    const n = Math.floor(Number(customTipText) || 0);
    if (n >= TIP_CONFIG.MIN_AMOUNT) { setTipAmount(n); setCustomTipOpen(false); }
  };

  const handleConfirm = async () => {
    if (confirming || !docId) return;
    // A tip was picked → hand off to the same payment page pay-after-work
    // orders use (mode:"pay_order", tipOnly:true), instead of charging it
    // inline here. That screen collects the tip (still changeable) and then
    // performs the same user-confirm call this branch used to do directly.
    if (tipAmount > 0) {
      navigate("/checkout-schedule/summary", {
        state: { mode: "pay_order", existingOrder: order, tipOnly: true, initialTip: tipAmount },
      });
      return;
    }
    setConfirming(true); setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/orders/${encodeURIComponent(docId)}/user-confirm/`, {
        method:  "POST",
        headers: authHeaders(),
        body:    JSON.stringify({ mobile: userMobile }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.error || d?.message || `Error ${res.status}`);
      }
      setSuccess(true);
      setTimeout(() => navigate("/profile-dashboard", { replace: true, state: { openBookings: true } }), 1200);
    } catch (e) {
      setError(e.message || "Confirm failed. Please try again.");
    } finally {
      setConfirming(false);
    }
  };

  if (success) {
    return (
      <div style={{ ...PS.page, display:"flex", alignItems:"center", justifyContent:"center" }}>
        <div style={PS.card}>
          <div style={{ fontSize:56, textAlign:"center" }}>✅</div>
          <h2 style={{ textAlign:"center", fontWeight:900, fontSize:24, margin:"14px 0 8px" }}>
            {tipAmount > 0 ? "Thank you!" : "Job Confirmed!"}
          </h2>
          <p style={{ textAlign:"center", color:"#6b7280", fontSize:14 }}>
            {tipAmount > 0 ? "Thanks for the tip! Redirecting home…" : "Returning to your orders…"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={PS.page}>
      <div style={PS.container}>
        <button style={PS.backBtn} onClick={() => navigate(-1)}>← Back</button>

        <div style={PS.header}>
          <p style={PS.eyebrow}>Job Completed</p>
          <h1 style={PS.title}>Confirm & Wrap Up</h1>
          <p style={PS.sub}>Your partner marked this job as done. Confirm below to close it out.</p>
        </div>

        <div style={PS.card}>
          <div style={PS.summarySection}>
            {orderId && (
              <div style={PS.row}>
                <span style={PS.rowLabel}>Order #</span>
                <span style={PS.rowVal}>{orderId}</span>
              </div>
            )}
            <div style={PS.row}>
              <span style={PS.rowLabel}>Service</span>
              <span style={{ ...PS.rowVal, maxWidth:"60%", textAlign:"right" }}>{serviceName}</span>
            </div>
            <div style={PS.row}>
              <span style={PS.rowLabel}>Payment</span>
              <span style={{ ...PS.rowVal, color:"#16a34a" }}>✓ Already paid</span>
            </div>
          </div>

          {/* ── Partner tip — how was the service? ─────────────────────── */}
          <div style={PS.tipBox}>
            <div style={PS.tipTitle}>Happy with the work? Add a tip 🙌</div>
            <div style={PS.tipSub}>100% goes to the partner who completed your job. Optional.</div>
            <div style={PS.tipChipRow}>
              {TIP_CONFIG.PRESETS.map((amt) => (
                <button key={amt} type="button"
                  style={{ ...PS.tipChip, ...(tipAmount === amt ? PS.tipChipActive : {}) }}
                  onClick={() => handleSelectTipPreset(amt)}>
                  {fmt(amt)}
                </button>
              ))}
              <button type="button"
                style={{ ...PS.tipChip, ...(customTipOpen ? PS.tipChipActive : {}) }}
                onClick={() => setCustomTipOpen((v) => !v)}>
                Custom
              </button>
              {tipAmount > 0 && (
                <button type="button" style={PS.tipRemoveBtn} onClick={handleRemoveTip}>
                  Remove tip ✕
                </button>
              )}
            </div>
            {customTipOpen && (
              <div style={PS.tipCustomRow}>
                <input
                  type="number"
                  min={TIP_CONFIG.MIN_AMOUNT}
                  placeholder={`Min ${fmt(TIP_CONFIG.MIN_AMOUNT)}`}
                  value={customTipText}
                  onChange={(e) => setCustomTipText(e.target.value)}
                  style={PS.tipCustomInput}
                />
                <button type="button" style={PS.tipCustomApplyBtn} onClick={handleCustomTipApply}>
                  Apply
                </button>
              </div>
            )}
            {customTipOpen && customTipText && Number(customTipText) < TIP_CONFIG.MIN_AMOUNT && (
              <div style={PS.tipMinHint}>Minimum tip is {fmt(TIP_CONFIG.MIN_AMOUNT)}.</div>
            )}
          </div>

          {error && (
            <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:10, padding:"10px 14px", color:"#b91c1c", fontSize:13, marginBottom:16 }}>
              {error}
            </div>
          )}

          <button
            style={{ ...PS.payBtn, opacity: confirming ? 0.7 : 1 }}
            disabled={confirming}
            onClick={handleConfirm}
          >
            {confirming
              ? "Processing…"
              : tipAmount > 0
                ? `Continue to Pay ${fmt(tipAmount)} Tip →`
                : "Confirm Job Done"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// RESCHEDULE PAGE — shown when partner cancels and user clicks "Reschedule"
// User must pick a new date/time for each service before confirming.
// payment_status 0 → normal rebooking
// payment_status 1 → prior payment credited / refunded notice shown
// ─────────────────────────────────────────────────────────────────────────────
const ReschedulePage = ({ order, userMobile, navigate }) => {
  const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

  const serviceName  = order?.service || order?.service_name || "Service Booking";
  const orderId      = String(order?.order_id || order?._id || "").slice(-8).toUpperCase();
  const totalAmount  = Number(order?.total_amount || order?.service_charge || order?.amount || 0);
  const address      = order?.delivery_address || null;
  const payMode      = order?.payment_mode || "pay_now";
  const instructions = order?.instructions || "";

  const alreadyPaid = Number(order?.payment_status) === 1 ||
    ["paid", "completed", "received"].includes(String(order?.payment_status || "").toLowerCase());

  // Build reschedule items with dates cleared so user must pick new slots
  const buildItems = () => {
    let src = [];
    if (Array.isArray(order?.items) && order.items.length > 0)         src = order.items;
    else if (Array.isArray(order?.services) && order.services.length > 0)
      src = order.services.map((s, i) => ({
        service: typeof s === "string" ? s : (s?.name || "Service"),
        qty: 1, price: 0, id: `svc-${i}`,
      }));
    else src = [{ service: serviceName, qty: 1, price: totalAmount }];

    return src.map((it, i) => ({
      id:            String(it.id || it.service_id || it.dbId || `item-${i}`),
      name:          it.service || it.name || it.title || `Service ${i + 1}`,
      qty:           Number(it.qty || 1),
      price:         Number(it.price || 0),
      bookingDate:   "",   // cleared — user picks a new date
      bookingTime:   "",
      bookingSlotId: "",
    }));
  };

  const [items, setItems]     = useState(buildItems);
  const [placing, setPlacing] = useState(false);
  const [error,   setError]   = useState("");
  const [success, setSuccess] = useState(false);

  // ── Slot modal state ──────────────────────────────────────────────────────
  const [slotOpen,     setSlotOpen]     = useState(false);
  const [activeId,     setActiveId]     = useState(null);
  const [draftDate,    setDraftDate]    = useState("");
  const [draftTime,    setDraftTime]    = useState("");
  const [draftSlotId,  setDraftSlotId]  = useState("");
  const [availDays,    setAvailDays]    = useState([]);
  const [availDate,    setAvailDate]    = useState("");
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [availError,   setAvailError]   = useState("");

  const activeItem = items.find(i => i.id === activeId) || null;

  const openSlotFor = async (item) => {
    setActiveId(item.id);
    setDraftDate(""); setDraftTime(""); setDraftSlotId("");
    setAvailDays([]); setAvailDate(""); setAvailError("");
    setSlotOpen(true); setLoadingAvail(true);
    try {
      const resp = await fetchSlotAvailability(item.id);
      const days = resp.days || [];
      setAvailDays(days);
      const first = days.find(d => d.slots.some(isSlotSelectable))?.date || days[0]?.date || "";
      setAvailDate(first);
    } catch (e) {
      setAvailError(e?.message || "Failed to load available slots.");
    } finally {
      setLoadingAvail(false);
    }
  };

  const closeSlot = () => {
    setSlotOpen(false); setActiveId(null);
    setDraftDate(""); setDraftTime(""); setDraftSlotId("");
    setAvailDays([]); setAvailDate(""); setAvailError("");
  };

  const saveSlot = () => {
    if (!activeId || !draftDate || !draftTime) return;
    const day  = availDays.find(d => d.date === draftDate);
    const slot = day?.slots?.find(s => s.time === draftTime);
    if (!isSlotSelectable(slot)) { alert("Selected slot is no longer available."); return; }
    setItems(prev => prev.map(i =>
      i.id === activeId
        ? { ...i, bookingDate: draftDate, bookingTime: draftTime, bookingSlotId: draftSlotId }
        : i
    ));
    closeSlot();
  };

  const allSlotsReady = items.every(i => i.bookingDate && i.bookingTime);

  const handleRebook = async () => {
    if (placing || !allSlotsReady) return;
    if (!userMobile) {
      navigate("/user/user-login", { state: { redirectTo: "/checkout-schedule/summary" } });
      return;
    }
    setPlacing(true); setError("");
    try {
      const payload = {
        mobile:            userMobile,
        items,
        total_amount:      totalAmount,
        delivery_address:  address || null,
        instructions,
        payment_option:    payMode,
        booking_type:      "scheduled",
        is_reschedule:     true,
        original_order_id: String(order?._id || order?.order_id || ""),
        booking_slots:     items.map(i => ({ service_id: i.id, date: i.bookingDate, time: i.bookingTime, slot_id: i.bookingSlotId })),
        ...(alreadyPaid ? { prior_payment_status: 1 } : {}),
      };
      const res  = await fetch(`${API_BASE_URL}/api/user/place-order/`, {
        method: "POST", credentials: "include", headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.message || `Error ${res.status}`);
      setSuccess(true);
      sessionStorage.setItem("refresh_orders", "1");
      setTimeout(() => navigate("/user/user-dashboard", { replace: true, state: { openBookings: true } }), 1800);
    } catch (e) {
      setError(e.message || "Failed to reschedule. Please try again.");
    } finally {
      setPlacing(false);
    }
  };

  if (success) {
    return (
      <div style={{ ...PS.page, display:"flex", alignItems:"center", justifyContent:"center" }}>
        <div style={PS.card}>
          <div style={{ fontSize:56, textAlign:"center" }}>✅</div>
          <h2 style={{ textAlign:"center", fontWeight:900, fontSize:22, margin:"14px 0 8px" }}>Rescheduled!</h2>
          <p style={{ textAlign:"center", color:"#6b7280", fontSize:14 }}>Your new booking has been placed.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={PS.page}>
      <div style={PS.container}>
        <button style={PS.backBtn} onClick={() => navigate(-1)}>← Back</button>

        <div style={PS.header}>
          <p style={PS.eyebrow}>Reschedule</p>
          <h1 style={PS.title}>Choose New Date & Time</h1>
          <p style={PS.sub}>
            Your partner cancelled this order. Pick new slots for each service to reschedule.
            {alreadyPaid && " Your prior payment will be credited or refunded."}
          </p>
        </div>

        {/* Order reference */}
        {orderId && (
          <div style={RS.refRow}>
            <span style={RS.refLabel}>Original order</span>
            <span style={RS.refVal}>#{orderId}</span>
          </div>
        )}

        {/* Per-service slot picker */}
        <div style={RS.itemList}>
          {items.map((item, i) => (
            <div key={i} style={RS.itemCard}>
              <div style={RS.itemTop}>
                <div style={RS.itemName}>
                  {item.name}
                  {item.qty > 1 && <span style={RS.itemQty}> ×{item.qty}</span>}
                </div>
                {item.price > 0 && <span style={RS.itemPrice}>{fmt(item.price)}</span>}
              </div>

              {item.bookingDate ? (
                <div style={RS.slotChosen}>
                  <span style={RS.slotChosenText}>📅 {item.bookingDate} · 🕐 {item.bookingTime}</span>
                  <button style={RS.changeBtn} onClick={() => openSlotFor(item)}>Change</button>
                </div>
              ) : (
                <button style={RS.pickBtn} onClick={() => openSlotFor(item)}>
                  📅 Select Date &amp; Time
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Amount summary */}
        <div style={RS.totalRow}>
          <span style={RS.totalLabel}>Total</span>
          <span style={RS.totalVal}>{fmt(totalAmount)}</span>
        </div>

        {/* Already-paid notice */}
        {alreadyPaid && (
          <div style={RS.paidNotice}>
            💳 You paid {fmt(totalAmount)} for the original booking. This will be credited or refunded once the reschedule is confirmed.
          </div>
        )}

        {!allSlotsReady && (
          <div style={RS.hint}>Select a date and time for each service above to continue.</div>
        )}

        {error && (
          <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:10, padding:"10px 14px", color:"#b91c1c", fontSize:13, margin:"10px 0" }}>
            {error}
          </div>
        )}

        <button
          style={{ ...PS.payBtn, marginTop:16, opacity: (!allSlotsReady || placing) ? 0.45 : 1 }}
          disabled={!allSlotsReady || placing}
          onClick={handleRebook}
        >
          {placing ? "Processing…" : "Confirm Reschedule"}
        </button>
        <button style={{ ...PS.laterBtn, marginTop:8 }} onClick={() => navigate(-1)}>
          Cancel
        </button>
      </div>

      {/* Slot selection modal — reuses the existing SlotModal component */}
      <SlotModal
        open={slotOpen}
        item={activeItem}
        availabilityDays={availDays}
        loadingAvailability={loadingAvail}
        availabilityError={availError}
        selectedAvailabilityDate={availDate}
        onSelectDate={(d) => {
          setAvailDate(d);
          if (draftDate !== d) { setDraftDate(d); setDraftTime(""); setDraftSlotId(""); }
        }}
        draftDate={draftDate}
        draftTime={draftTime}
        onSelectTime={(date, time, slotId) => { setDraftDate(date); setDraftTime(time); setDraftSlotId(slotId || ""); }}
        onClose={closeSlot}
        onSave={saveSlot}
      />
    </div>
  );
};

// Reschedule page styles
const RS = {
  refRow:        { display:"flex", justifyContent:"space-between", alignItems:"center", background:"#f1f5f9", borderRadius:10, padding:"8px 14px", marginBottom:14, fontSize:13 },
  refLabel:      { color:"#6b7280", fontWeight:500 },
  refVal:        { fontWeight:700, color:"#0f172a" },
  itemList:      { display:"flex", flexDirection:"column", gap:10, marginBottom:14 },
  itemCard:      { background:"#fff", border:"1px solid #e2e8f0", borderRadius:14, padding:"14px 16px" },
  itemTop:       { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 },
  itemName:      { fontSize:14, fontWeight:700, color:"#0f172a" },
  itemQty:       { color:"#6b7280", fontWeight:500 },
  itemPrice:     { fontSize:14, fontWeight:700, color:"#1d4ed8" },
  pickBtn:       { width:"100%", padding:"10px 0", borderRadius:10, background:"#eff6ff", color:"#1d4ed8", fontSize:13, fontWeight:700, border:"1.5px dashed #93c5fd", cursor:"pointer" },
  slotChosen:    { display:"flex", alignItems:"center", justifyContent:"space-between", background:"#f0fdf4", border:"1px solid #86efac", borderRadius:10, padding:"8px 12px" },
  slotChosenText:{ fontSize:13, color:"#14532d", fontWeight:600 },
  changeBtn:     { background:"none", border:"none", color:"#1d4ed8", fontSize:12, fontWeight:700, cursor:"pointer", padding:"2px 6px" },
  totalRow:      { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 4px", borderTop:"2px solid #e5e7eb" },
  totalLabel:    { fontSize:15, fontWeight:700, color:"#374151" },
  totalVal:      { fontSize:20, fontWeight:900, color:"#1d4ed8" },
  paidNotice:    { background:"#fef3c7", border:"1px solid #fcd34d", borderRadius:10, padding:"10px 14px", fontSize:13, color:"#92400e", margin:"10px 0" },
  hint:          { fontSize:12, color:"#9ca3af", textAlign:"center", margin:"8px 0" },
};

// ─────────────────────────────────────────────────────────────────────────────
const SummaryPageContent = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const loggedInUser = useMemo(() => getLoggedInUser(), []);
  const userName     = loggedInUser?.name || loggedInUser?.full_name || "Guest";
  const userMobile   = loggedInUser?.mobile || loggedInUser?.phone || location.state?.user?.mobile || "";
  // Prefer user_id for address lookup — dashboard also saves by user_id
  const addressKey   = loggedInUser?.id || loggedInUser?.user_id || userMobile;

  // ── Cart — resolved once on mount, normalised ────────────────────────────
  const [cartItems, setCartItemsRaw] = useState(() => resolveCart(location.state));

  // Debug: log what we resolved so you can see in console if empty
  useEffect(() => {
    console.log("[SummaryPage] resolved cart:", cartItems);
  }, []); // eslint-disable-line

  const setCartItems = useCallback((updater) => {
    setCartItemsRaw((prev) => {
      const next = normalizeCart(typeof updater === "function" ? updater(prev) : updater);
      writeLocalCart(next);
      return next;
    });
  }, []);

  // Listen for cart changes in other tabs
  useEffect(() => {
    const handler = () => setCartItemsRaw(readLocalCart());
    window.addEventListener("storage",      handler);
    window.addEventListener("cart-updated", handler);
    return () => {
      window.removeEventListener("storage",      handler);
      window.removeEventListener("cart-updated", handler);
    };
  }, []);

  // Fetch saved addresses for logged-in user
  useEffect(() => {
    if (!addressKey) return;
    setAddrLoading(true);
    fetch(`${API_BASE_URL}/api/user/user-addresses/${encodeURIComponent(addressKey)}/`, {
      credentials: "include", headers: authHeaders(),
    })
      .then(r => r.json())
      .then(data => {
        const addrs = Array.isArray(data?.data) ? data.data : [];
        setSavedAddresses(addrs);
        if (addrs.length) setSelectedAddrId(addrs[0].id);
      })
      .catch(e => console.warn("[Addresses] fetch error:", e.message))
      .finally(() => setAddrLoading(false));
  }, [addressKey]); // eslint-disable-line

  // ── UI state ─────────────────────────────────────────────────────────────
  const [instructions,  setInstructions]  = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [placing,       setPlacing]       = useState(false);
  const [placeError,    setPlaceError]    = useState("");

  // ── Saved addresses ───────────────────────────────────────────────────────
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [addrLoading,    setAddrLoading]    = useState(false);
  const [selectedAddrId, setSelectedAddrId] = useState("");
  const selectedAddress = useMemo(
    () => savedAddresses.find(a => a.id === selectedAddrId) || null,
    [savedAddresses, selectedAddrId]
  );

  // ── Current Location (Google API when configured, else Testing Mode) ───────
  const [locationMode,        setLocationMode]        = useState("saved"); // "saved" | "current"
  const [currentLocationAddr, setCurrentLocationAddr]  = useState(null);
  const [testLocationId,      setTestLocationId]       = useState("");

  // Testing Mode — no Google API key configured yet, so pick a dummy location instead.
  const handleSelectTestLocation = useCallback((id) => {
    setTestLocationId(id);
    setCurrentLocationAddr(DUMMY_TEST_LOCATIONS.find(l => l.id === id) || null);
  }, []);

  // Whichever location source is active — this is what actually gets booked.
  const effectiveAddress = locationMode === "current" ? currentLocationAddr : selectedAddress;

  // ── Slot modal ────────────────────────────────────────────────────────────
  const [slotModalOpen,            setSlotModalOpen]            = useState(false);
  const [selectedItemId,           setSelectedItemId]           = useState(null);
  const [draftDate,                setDraftDate]                = useState("");
  const [draftTime,                setDraftTime]                = useState("");
  const [draftSlotId,              setDraftSlotId]              = useState("");
  const [availabilityDays,         setAvailabilityDays]         = useState([]);
  const [loadingAvailability,      setLoadingAvailability]      = useState(false);
  const [availabilityError,        setAvailabilityError]        = useState("");
  const [selectedAvailabilityDate, setSelectedAvailabilityDate] = useState("");

  const selectedItem = useMemo(
    () => cartItems.find((i) => String(i.id) === String(selectedItemId)) || null,
    [cartItems, selectedItemId]
  );

  // ── Derived totals ────────────────────────────────────────────────────────
  const subtotal      = useMemo(() => cartItems.reduce((s,i) => s + Number(i.price||0)*Number(i.qty||1), 0), [cartItems]);
  const totalQty      = cartItems.reduce((s,i) => s+Number(i.qty||1), 0);
  const allSlotsReady = cartItems.every((i) => isShoppingItem(i) ? true : (i.bookingDate && i.bookingTime));
  const hasDisabledItems = cartItems.some((i) => !isServiceEnabled(String(i.id)));

  // Tax/Platform Fee are admin-editable (Dashboard's Seat Availability tab ->
  // Checkout Fees card) and synced via fetchRemoteConfig() onto
  // CONFIG.CHECKOUT_CHARGES. That fetch may already be in flight/settled from
  // app load, but if this page is the very first one visited this session it
  // hasn't necessarily resolved yet — re-fetch (a no-op if already done) and
  // force one re-render once it lands so the breakdown below always reflects
  // the latest saved values, not just whatever was cached at module load.
  const [configVersion, forceConfigRerender] = useState(0);
  useEffect(() => {
    fetchRemoteConfig().then(() => forceConfigRerender((n) => n + 1));
  }, []);

  // ── Charges (all driven by config) ───────────────────────────────────────
  const CHARGES          = CONFIG.CHECKOUT_CHARGES ?? { DISCOUNT_PERCENT: 5, GST_PERCENT: 18, PLATFORM_FEE: 20 };
  const discount          = Math.round(subtotal * (CHARGES.DISCOUNT_PERCENT / 100));
  const taxableBeforeCoupon = subtotal - discount;

  // ── Coupon code (CND02 etc. — see coupons_engine.py) ─────────────────────
  // Validation only computes the discount for preview; it never marks the
  // coupon used. Redemption happens only after the order is actually placed
  // (pay_after_work: right here in handlePlaceOrder; pay_now: in
  // PaymentPage's finishAfterVerify, once payment is verified).
  const [couponCode,     setCouponCode]     = useState("");
  const [couponChecking, setCouponChecking] = useState(false);
  const [couponError,    setCouponError]    = useState("");
  const [couponResult,   setCouponResult]   = useState(null); // { coupon_code, discount_percent, discount_amount }

  const couponDiscountAmount = couponResult?.discount_amount || 0;
  const taxableAmt           = Math.max(0, taxableBeforeCoupon - couponDiscountAmount);

  const handleApplyCoupon = async () => {
    const code = couponCode.trim();
    if (!code || !userMobile) return;
    setCouponChecking(true);
    setCouponError("");
    try {
      const res  = await fetch(`${API_BASE_URL}/api/user/coupon/validate/`, {
        method:"POST", credentials:"include", headers:authHeaders(),
        body: JSON.stringify({ mobile:userMobile, coupon_code:code, amount:taxableBeforeCoupon }),
      });
      const data = await res.json();
      const result = data?.data || data;
      if (!res.ok || !result?.valid) {
        setCouponResult(null);
        setCouponError(result?.message || data?.message || "This coupon code is not valid.");
      } else {
        setCouponResult(result);
      }
    } catch (err) {
      setCouponResult(null);
      setCouponError("Could not check coupon code. Please try again.");
    } finally {
      setCouponChecking(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode(""); setCouponResult(null); setCouponError("");
  };

  // Cart contents changed (item removed/qty changed) after a coupon was
  // already applied — its discount_amount was computed against the old
  // taxableBeforeCoupon, so drop it rather than silently applying a stale
  // discount. The user can just re-apply the same code.
  useEffect(() => {
    if (couponResult) { setCouponResult(null); setCouponError(""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxableBeforeCoupon]);

  // Tax and Platform Fee are per-service (Dashboard's Enable/Disable table
  // lets an admin override either for a specific service; absent = the
  // global CHARGES default above). Tax is summed per cart item on its own
  // proportional share of the order-wide discount — with no overrides set
  // this collapses back to the exact old flat calc, since every item then
  // shares the same CHARGES.GST_PERCENT. Platform Fee is summed once per
  // DISTINCT service in the cart, not per unit quantity — two units of the
  // same service still only pay that service's fee once.
  const gst = useMemo(() => {
    if (subtotal <= 0) return 0;
    return Math.round(cartItems.reduce((sum, item) => {
      const itemTotal   = Number(item.price || 0) * Number(item.qty || 1);
      const itemTaxable = itemTotal * (taxableAmt / subtotal);
      const { taxPercent } = getServiceFees(String(item.id));
      return sum + itemTaxable * (taxPercent / 100);
    }, 0));
  }, [cartItems, subtotal, taxableAmt, configVersion]);

  const platformFee = useMemo(() => {
    const seen = new Set();
    let sum = 0;
    cartItems.forEach((item) => {
      const key = String(item.id);
      if (seen.has(key)) return;
      seen.add(key);
      sum += getServiceFees(key).platformFee;
    });
    return sum;
  }, [cartItems, configVersion]);

  // Display-only blended rate — always mathematically accurate whether every
  // item shares CHARGES.GST_PERCENT (the common case, no overrides set) or
  // items carry different per-service tax rates.
  const effectiveTaxPercent = taxableAmt > 0 ? Math.round((gst / taxableAmt) * 1000) / 10 : CHARGES.GST_PERCENT;

  // ── Payment mode ──────────────────────────────────────────────────────────
  // subtotal < threshold  → must pay now (no choice)
  // subtotal ≥ threshold  → user can choose pay_now or pay_after_work
  const PARTIAL_THRESHOLD = CONFIG.PARTIAL_PAYMENT_THRESHOLD ?? 1000;
  const canPayLater       = subtotal >= PARTIAL_THRESHOLD;
  const [paymentOption, setPaymentOption] = useState("pay_now");

  // This page is the Schedule checkout — booking_type is always "schedule".
  // Instant checkout lives in its own file/route: InstantSummaryPage.js at
  // /checkout-instant/summary.
  const bookingType = "schedule";

  // ── Partner tip (optional, added on top of the bill before confirmation) ──
  const TIP_CONFIG   = CONFIG.PARTNER_TIP ?? { MIN_AMOUNT: 10, PRESETS: [10, 20, 50, 100] };
  const [tipAmount,     setTipAmount]     = useState(0);
  const [customTipOpen, setCustomTipOpen] = useState(false);
  const [customTipText, setCustomTipText] = useState("");

  const handleSelectTipPreset = (amt) => {
    setCustomTipOpen(false); setCustomTipText("");
    setTipAmount((prev) => (prev === amt ? 0 : amt));
  };
  const handleRemoveTip = () => { setTipAmount(0); setCustomTipOpen(false); setCustomTipText(""); };
  const handleCustomTipApply = () => {
    const n = Math.floor(Number(customTipText) || 0);
    if (n >= TIP_CONFIG.MIN_AMOUNT) { setTipAmount(n); setCustomTipOpen(false); }
  };

  const total = taxableAmt + gst + platformFee + tipAmount;

  // ── Delete item ───────────────────────────────────────────────────────────
  const handleDeleteService = useCallback((itemId) => {
    setCartItems((prev) => {
      const item = prev.find((i) => String(i.id) === String(itemId));
      if (item && userMobile) apiCartRemoveItem(userMobile, item);
      return prev.filter((i) => String(i.id) !== String(itemId));
    });
    if (String(selectedItemId) === String(itemId)) resetModalState();
  }, [selectedItemId, userMobile]); // eslint-disable-line

  // ── Slot handlers ─────────────────────────────────────────────────────────
  const openSlotModal = async (item) => {
    setSelectedItemId(item.id);
    setDraftDate(item.bookingDate||""); setDraftTime(item.bookingTime||"");
    setDraftSlotId(item.bookingSlotId||"");
    setSlotModalOpen(true); setLoadingAvailability(true);
    setAvailabilityError(""); setAvailabilityDays([]); setSelectedAvailabilityDate("");
    try {
      const resp = await fetchSlotAvailability(item.id);
      const days = resp.days||[];
      setAvailabilityDays(days);
      const preferred =
        (item.bookingDate && days.find((d)=>d.date===item.bookingDate)?.date) ||
        days.find((d)=>d.slots.some(isSlotSelectable))?.date || days[0]?.date||"";
      setSelectedAvailabilityDate(preferred);
    } catch (e) { setAvailabilityError(e?.message||"Failed to fetch availability."); }
    finally { setLoadingAvailability(false); }
  };

  const handleSelectAvailabilityDate = (date) => {
    setSelectedAvailabilityDate(date);
    if (draftDate !== date) { setDraftTime(""); setDraftSlotId(""); setDraftDate(date); }
  };

  const handleSelectTime = (date, time, slotId) => {
    setDraftDate(date); setDraftTime(time); setDraftSlotId(slotId||"");
  };

  const resetModalState = () => {
    setSlotModalOpen(false); setSelectedItemId(null);
    setDraftDate(""); setDraftTime(""); setDraftSlotId("");
    setAvailabilityDays([]); setLoadingAvailability(false);
    setAvailabilityError(""); setSelectedAvailabilityDate("");
  };

  const handleSaveSlot = () => {
    if (!selectedItemId || !draftDate || !draftTime) return;
    const day  = availabilityDays.find((d)=>d.date===draftDate);
    const slot = day?.slots?.find((s)=>s.time===draftTime);
    if (!isSlotSelectable(slot)) { alert("Selected slot is no longer available."); return; }
    setCartItems((prev) => {
      const updated = prev.map((i) =>
        String(i.id)===String(selectedItemId)
          ? { ...i, bookingDate:draftDate, bookingTime:draftTime, bookingSlotId:draftSlotId }
          : i
      );
      const updatedItem = updated.find((i)=>String(i.id)===String(selectedItemId));
      if (updatedItem && userMobile) apiCartAdd(userMobile, [updatedItem]);
      return updated;
    });
    resetModalState();
  };

  // ── Place order ───────────────────────────────────────────────────────────
  // pay_after_work → place order directly (payment happens post-job)
  // pay_now        → create Razorpay order → navigate to /payment page
  const handlePlaceOrder = async () => {
    if (!agreedToTerms || !allSlotsReady || placing || hasDisabledItems) return;
    if (!userMobile) {
      navigate("/user/user-login", { state:{ redirectTo:"/checkout-schedule/summary" } });
      return;
    }

    const orderPayload = {
      mobile:                    userMobile,
      customer_name:             userName,
      items:                     cartItems,
      total_amount:              total,
      selected_city:             getUserCity(),
      instructions,
      delivery_address:          effectiveAddress || null,
      booking_slots:             cartItems.map((i) => ({ service_id:String(i.id), date:i.bookingDate||"", time:i.bookingTime||"", slot_id:i.bookingSlotId||"" })),
      booking_type:              bookingType,
      payment_option:            paymentOption,
      partial_payment_threshold: PARTIAL_THRESHOLD,
      tip_amount:                tipAmount,
      coupon_code:               couponResult?.coupon_code || "",
      coupon_discount_amount:    couponDiscountAmount,
    };

    setPlacing(true);
    setPlaceError("");

    // ── Pay After Work: place order immediately, no upfront payment ──────────
    if (paymentOption === "pay_after_work") {
      try {
        const data = await apiPlaceOrder(orderPayload);
        if (couponResult?.coupon_code && data?.data?.order_id) {
          try {
            await fetch(`${API_BASE_URL}/api/user/coupon/redeem/`, {
              method:"POST", credentials:"include", headers:authHeaders(),
              body: JSON.stringify({ mobile:userMobile, coupon_code:couponResult.coupon_code, booking_id:data.data.order_id }),
            });
          } catch (e) { console.warn("[Coupon] redeem error:", e.message); }
        }
        await apiCartClear(userMobile);
        clearLocalCart();
        setCartItemsRaw([]);
        // A tip was added → skip the bookings dashboard, go straight home.
        if (tipAmount > 0) {
          navigate("/", { replace:true, state:{ tipThanks:true } });
        } else {
          navigate("/profile-dashboard", { replace:true, state:{ openBookings:true, orderPlaced:true, orderId:data?.data?.order_id||"", total, mobile:userMobile } });
        }
      } catch (err) {
        const msg = err.message || "Failed to place order. Please try again.";
        setPlaceError(msg);
        if (err.status === 409) {
          const affectedItem = cartItems.find((i) => msg.includes(`"${i.name}"`) || msg.includes(`"${i.title}"`)) || cartItems.find((i) => i.bookingDate && i.bookingTime);
          if (affectedItem) {
            setCartItems((prev) => prev.map((i) => i.id === affectedItem.id ? { ...i, bookingDate:"", bookingTime:"", bookingSlotId:"" } : i));
            setTimeout(() => openSlotModal({ ...affectedItem, bookingDate:"", bookingTime:"", bookingSlotId:"" }), 200);
          }
        }
      } finally {
        setPlacing(false);
      }
      return;
    }

    // ── Pay Now: create Razorpay order → redirect to /payment ───────────────
    try {
      const res  = await fetch(`${API_BASE_URL}/api/payment/create-order/`, {
        method:  "POST",
        headers: authHeaders(),
        body:    JSON.stringify({ amount: total, mobile: userMobile }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.message || "Could not initiate payment.");
      navigate("/payment", {
        state: {
          paymentOrder: data,
          orderPayload,
          breakdown: {
            subtotal, discount, gst, platformFee, tip: tipAmount, total,
            coupon: couponResult?.coupon_code
              ? { code: couponResult.coupon_code, amount: couponDiscountAmount }
              : null,
          },
        },
      });
    } catch (err) {
      setPlaceError(err.message || "Payment initiation failed. Please try again.");
    } finally {
      setPlacing(false);
    }
  };

  // ── Empty cart — show browse button, NOT a blank page ────────────────────
  if (cartItems.length === 0) {
    return (
      <div style={S.page}>
        <div style={{ ...S.container, textAlign:"center", paddingTop:"90px" }}>
          <div style={{ fontSize:64 }}>🛒</div>
          <h2 style={{ fontSize:"30px", fontWeight:900, margin:"18px 0 10px" }}>Your cart is empty</h2>
          <p style={{ color:"#64748b", fontSize:"16px", marginBottom:"28px" }}>
            Add a service to continue with booking.
          </p>
          <button style={S.primaryBtn} onClick={() => navigate("/services/all-services")}>
            Browse Services
          </button>
          <br/>
          <button style={{ ...S.secondaryBtn, marginTop:12 }} onClick={() => navigate("/profile-dashboard", { state:{ openCart:true } })}>
            View Cart in Profile →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.container}>

        {/* Hero */}
        <div style={S.topHero}>
          <div>
            <p style={S.eyebrow}>Checkout</p>
            <h1 style={S.pageTitle}>Review and complete your booking</h1>
            <p style={S.pageSubtitle}>Each service can have its own booking slot.</p>
          </div>
          <div style={S.payableCard}>
            <div style={S.payableLabel}>Amount payable</div>
            <div style={S.payableValue}>{fmt(total)}</div>
            <div style={S.payableMeta}>{cartItems.length} services · {totalQty} items</div>
            <div style={paymentOption === "pay_after_work" ? S.payLaterBadge : S.payNowBadge}>
              {paymentOption === "pay_after_work"
                ? "⏳ Pay after job is done"
                : canPayLater
                  ? "💳 Pay Now selected"
                  : "💳 Pay at checkout"}
            </div>
          </div>
        </div>

        <StepProgress />

        <div style={S.mainGrid}>

          {/* ── LEFT ── */}
          <div style={S.leftColumn}>

            {/* Address */}
            <section style={S.card}>
              <SectionTitle title="Service Address" desc="Select where the professional should arrive."
                right={
                  <button style={S.ghostBtn}
                    onClick={() => navigate("/profile-dashboard", { state:{ openAddresses: true } })}>
                    Manage Addresses →
                  </button>
                } />

              {/* Saved Address / Current Location tabs */}
              <div style={S.addrTabRow}>
                <button type="button" style={{ ...S.addrTab, ...(locationMode === "saved" ? S.addrTabActive : {}) }}
                  onClick={() => setLocationMode("saved")}>
                  📍 Saved Address
                </button>
                <button type="button" style={{ ...S.addrTab, ...(locationMode === "current" ? S.addrTabActive : {}) }}
                  onClick={() => setLocationMode("current")}>
                  🛰 Current Location
                </button>
              </div>

              {locationMode === "saved" ? (
                addrLoading ? (
                  <div style={S.addrStateBox}>Loading your saved addresses…</div>
                ) : savedAddresses.length === 0 ? (
                  <div style={S.addrStateBox}>
                    <div style={{ fontSize:32, marginBottom:8 }}>📍</div>
                    <div style={{ fontWeight:700, marginBottom:6 }}>No saved addresses</div>
                    <div style={{ fontSize:13, color:"#64748b", marginBottom:14 }}>
                      Add up to 3 addresses in your profile to use here.
                    </div>
                    <button style={S.outlineBtn}
                      onClick={() => navigate("/profile-dashboard", { state:{ openAddresses: true } })}>
                      + Add Address in Profile
                    </button>
                  </div>
                ) : (
                  <div style={S.addressLayout}>
                    {/* Selectable address list */}
                    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                      {savedAddresses.map(addr => {
                        const active = selectedAddrId === addr.id;
                        const fullAddr = [addr.line1, addr.area, addr.city, addr.state, addr.pincode]
                          .filter(Boolean).join(", ");
                        return (
                          <div key={addr.id}
                            style={{ ...S.addressCard, ...(active ? S.addressCardSelected : {}), cursor:"pointer" }}
                            onClick={() => setSelectedAddrId(addr.id)}>
                            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                              <input type="radio" readOnly checked={active} style={{ accentColor:"#4f46e5", width:16, height:16 }} />
                              <span style={{ ...S.addrBadge, ...(active ? { background:"#eef2ff", color:"#4338ca", border:"1px solid #c7d2fe" } : {}) }}>
                                {ADDR_LABEL_ICONS[addr.label] || "📍"} {addr.label || "Address"}
                              </span>
                            </div>
                            <div style={S.addressBlock}>{fullAddr}</div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Map preview of selected address */}
                    <div style={S.mapCard}>
                      {selectedAddress && (
                        <GoogleMapPreview addr={selectedAddress} height={200}
                          emptyHint="Add address details in My Addresses" />
                      )}
                    </div>
                  </div>
                )
              ) : (
                <div>
                  {!IS_GOOGLE_CONFIGURED && (
                    <div style={S.testingBadge}>
                      🧪 Testing Mode — no Google Maps API key configured yet. Pick a dummy location below.
                      Once a real key is added to <code>config.js</code>, this switches to real GPS detection automatically.
                    </div>
                  )}

                  {IS_GOOGLE_CONFIGURED ? (
                    <CurrentLocationDetector onDetected={setCurrentLocationAddr}
                      buttonStyle={S.detectBtn} errorStyle={S.locationErrorText} />
                  ) : (
                    <select style={S.testSelect} value={testLocationId}
                      onChange={(e) => handleSelectTestLocation(e.target.value)}>
                      <option value="">Select a test location…</option>
                      {DUMMY_TEST_LOCATIONS.map(l => (
                        <option key={l.id} value={l.id}>{l.line1}, {l.city}</option>
                      ))}
                    </select>
                  )}

                  {currentLocationAddr && (
                    <div style={{ ...S.addressLayout, marginTop: "14px" }}>
                      <CurrentLocationCard addr={currentLocationAddr} onChange={setCurrentLocationAddr}
                        cardStyle={S.addressCard}
                        badgeStyle={{ ...S.addrBadge, background:"#eef2ff", color:"#4338ca", border:"1px solid #c7d2fe" }}
                        blockStyle={S.addressBlock} />

                      <div style={S.mapCard}>
                        <GoogleMapPreview addr={currentLocationAddr} height={200}
                          emptyTitle="Map preview needs a Google Maps API key" />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* Booking Slots */}
            <section style={S.card}>
              <SectionTitle title="Booking Slots Per Service"
                desc="Different services can have different dates and time slots."
                right={<div style={allSlotsReady ? S.slotStatusOk : S.slotStatusPending}>
                  {allSlotsReady ? "All slots selected" : "Slots pending"}
                </div>} />
              <div style={S.serviceSlotList}>
                {cartItems.map((item, index) => {
                  const shopping = isShoppingItem(item);
                  const ready    = shopping ? true : Boolean(item.bookingDate && item.bookingTime);
                  return (
                    <div key={item.id||index} style={S.serviceSlotCard}>
                      <div style={S.serviceSlotLeft}>
                        {item.image
                          ? <img src={item.image} alt={item.name} style={S.summaryImg} />
                          : <div style={S.summaryImgFallback}>{item.name?.charAt(0)||"S"}</div>
                        }
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={S.summaryItemTop}>
                            <h4 style={S.summaryItemName}>{item.name||item.title}</h4>
                            <div style={S.summaryItemPrice}>{fmt(Number(item.price||0)*Number(item.qty||1))}</div>
                          </div>
                          <div style={S.summaryItemDesc}>{item.short||item.subtitle||"Professional service"}</div>
                          <div style={{ marginTop:10 }}>
                            <span style={{ ...S.slotPill, ...(ready?S.slotPillActive:S.slotPillPending) }}>
                              {shopping ? "📦 No scheduling needed" : formatSlotLabel(item)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                        {/* Shopping products need no date/slot — just delivered.
                            Already has a slot (Schedule Booking, Reschedule, etc.) — no
                            inline editing; go back to the originating page to change it.
                            Only items with NO slot yet (regular Add to Cart flow) get a
                            picker, since this page is still their only way to set one. */}
                        {!shopping && !(item.bookingDate && item.bookingTime) && (
                          <button style={S.slotEditBtn} onClick={() => openSlotModal(item)}>
                            Select Slot
                          </button>
                        )}
                        <button style={S.deleteIconBtn} onClick={() => handleDeleteService(item.id)}
                          title={`Delete ${item.name}`}>🗑</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Instructions */}
            <section style={S.card}>
              <SectionTitle title="Additional Instructions" desc="Share any note for the professional." />
              <textarea value={instructions} onChange={(e) => setInstructions(e.target.value)}
                placeholder="E.g. Please call before arrival..." style={S.textarea} />
            </section>

            {/* Footer CTA */}
            <section style={S.footerActionCard}>
              {placeError && <div style={S.errorBanner}>{placeError}</div>}
              {hasDisabledItems && (
                <div style={S.disabledServiceBanner}>
                  ⏸ One or more services in your cart are currently stopped. We will get back to you shortly. Please remove them or check back later.
                </div>
              )}
              {!allSlotsReady && (
                <div style={S.warnBanner}>
                  ⚠ Please select a booking slot for every service before proceeding.
                </div>
              )}

              {/* ── Partner tip ─────────────────────────────────────────── */}
              <div style={S.tipBox}>
                <div style={S.tipTitle}>Add a tip for your service partner 🙌</div>
                <div style={S.tipSub}>100% goes to the partner who completes your job. Optional.</div>
                <div style={S.tipChipRow}>
                  {TIP_CONFIG.PRESETS.map((amt) => (
                    <button key={amt} type="button"
                      style={{ ...S.tipChip, ...(tipAmount === amt ? S.tipChipActive : {}) }}
                      onClick={() => handleSelectTipPreset(amt)}>
                      {fmt(amt)}
                    </button>
                  ))}
                  <button type="button"
                    style={{ ...S.tipChip, ...(customTipOpen ? S.tipChipActive : {}) }}
                    onClick={() => setCustomTipOpen((v) => !v)}>
                    Custom
                  </button>
                  {tipAmount > 0 && (
                    <button type="button" style={S.tipRemoveBtn} onClick={handleRemoveTip}>
                      Remove tip ✕
                    </button>
                  )}
                </div>
                {customTipOpen && (
                  <div style={S.tipCustomRow}>
                    <input
                      type="number"
                      min={TIP_CONFIG.MIN_AMOUNT}
                      placeholder={`Min ${fmt(TIP_CONFIG.MIN_AMOUNT)}`}
                      value={customTipText}
                      onChange={(e) => setCustomTipText(e.target.value)}
                      style={S.tipCustomInput}
                    />
                    <button type="button" style={S.tipCustomApplyBtn} onClick={handleCustomTipApply}>
                      Apply
                    </button>
                  </div>
                )}
                {customTipOpen && customTipText && Number(customTipText) < TIP_CONFIG.MIN_AMOUNT && (
                  <div style={S.tipMinHint}>Minimum tip is {fmt(TIP_CONFIG.MIN_AMOUNT)}.</div>
                )}
                {tipAmount > 0 && (
                  <div style={S.tipConfirm}>Thank you! {fmt(tipAmount)} will be added to the partner's payout.</div>
                )}
              </div>

              {/* ── Coupon code ─────────────────────────────────────────── */}
              <div style={S.payBreakdownBox}>
                <div style={S.payBreakdownTitle}>Have a coupon?</div>
                {couponResult?.coupon_code ? (
                  <div style={S.couponAppliedRow}>
                    <span style={{ color:"#16a34a", fontWeight:600 }}>
                      ✓ {couponResult.coupon_code} applied — {couponResult.discount_percent}% off
                    </span>
                    <button type="button" style={S.couponRemoveBtn} onClick={handleRemoveCoupon}>Remove</button>
                  </div>
                ) : (
                  <div style={S.couponInputRow}>
                    <input
                      type="text"
                      placeholder="Enter coupon code"
                      value={couponCode}
                      onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(""); }}
                      style={S.couponInput}
                    />
                    <button
                      type="button"
                      style={S.couponApplyBtn}
                      disabled={!couponCode.trim() || couponChecking}
                      onClick={handleApplyCoupon}
                    >
                      {couponChecking ? "Checking…" : "Apply"}
                    </button>
                  </div>
                )}
                {couponError && <div style={S.couponErrorText}>{couponError}</div>}
              </div>

              {/* ── Payment breakdown ───────────────────────────────────── */}
              <div style={S.payBreakdownBox}>
                <div style={S.payBreakdownTitle}>Payment Breakdown</div>
                <div style={S.payBreakdownRows}>
                  <div style={S.payBreakdownRow}>
                    <span>Base Fare</span>
                    <span>{fmt(subtotal)}</span>
                  </div>
                  <div style={S.payBreakdownRow}>
                    <span style={{ color:"#16a34a" }}>
                      Discount ({CHARGES.DISCOUNT_PERCENT}%)
                    </span>
                    <span style={{ color:"#16a34a" }}>−{fmt(discount)}</span>
                  </div>
                  {couponDiscountAmount > 0 && (
                    <div style={S.payBreakdownRow}>
                      <span style={{ color:"#16a34a" }}>Coupon ({couponResult.coupon_code})</span>
                      <span style={{ color:"#16a34a" }}>−{fmt(couponDiscountAmount)}</span>
                    </div>
                  )}
                  <div style={S.payBreakdownRow}>
                    <span>Tax ({effectiveTaxPercent}%)</span>
                    <span>{fmt(gst)}</span>
                  </div>
                  <div style={S.payBreakdownRow}>
                    <span>Platform Fee</span>
                    <span>{fmt(platformFee)}</span>
                  </div>
                  {tipAmount > 0 && (
                    <div style={S.payBreakdownRow}>
                      <span>Partner Tip</span>
                      <span>{fmt(tipAmount)}</span>
                    </div>
                  )}
                  <div style={{ ...S.payBreakdownRow, ...S.payBreakdownTotal }}>
                    <span>Total Payable</span>
                    <span>{fmt(total)}</span>
                  </div>
                </div>
                {(discount > 0 || couponDiscountAmount > 0) && (
                  <div style={S.saveNote}>🎉 You save {fmt(discount + couponDiscountAmount)} on this booking</div>
                )}
              </div>

              {/* ── Payment option selector (only when total ≥ threshold) ── */}
              {canPayLater && (
                <div style={S.payOptionWrap}>
                  <div style={S.payOptionTitle}>How would you like to pay?</div>
                  <div style={S.payOptionRow}>
                    <button
                      style={{ ...S.payOptionBtn, ...(paymentOption === "pay_now" ? S.payOptionBtnActive : {}) }}
                      onClick={() => setPaymentOption("pay_now")}
                    >
                      <div style={S.payOptionIcon}>💳</div>
                      <div style={S.payOptionTextWrap}>
                        <div style={S.payOptionLabel}>Pay Now</div>
                        <div style={S.payOptionSub}>Pay {fmt(total)} at checkout</div>
                      </div>
                      {paymentOption === "pay_now" && <div style={S.payOptionCheck}>✓</div>}
                    </button>
                    <button
                      style={{ ...S.payOptionBtn, ...(paymentOption === "pay_after_work" ? S.payOptionBtnActive : {}) }}
                      onClick={() => setPaymentOption("pay_after_work")}
                    >
                      <div style={S.payOptionIcon}>⏳</div>
                      <div style={S.payOptionTextWrap}>
                        <div style={S.payOptionLabel}>Pay After Work</div>
                        <div style={S.payOptionSub}>Pay when the job is done</div>
                      </div>
                      {paymentOption === "pay_after_work" && <div style={S.payOptionCheck}>✓</div>}
                    </button>
                  </div>
                </div>
              )}

              <label style={S.termsWrap}>
                <input type="checkbox" checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)} />
                <span>
                  I agree to the{" "}
                  <a href="/legal-information" style={S.inlineLink}>Terms & Conditions</a> and{" "}
                  <a href="/legal-information" style={S.inlineLink}>Privacy Policy</a>
                </span>
              </label>
              <div style={S.footerBtns}>
                <button style={S.secondaryBtn} onClick={() => navigate(-1)}>← Back</button>
                <button
                  style={{ ...S.primaryBtn, ...(!agreedToTerms||!allSlotsReady||placing||hasDisabledItems?S.disabledBtn:{}) }}
                  disabled={!agreedToTerms||!allSlotsReady||placing||hasDisabledItems}
                  onClick={handlePlaceOrder}>
                  {placing
                    ? "Placing Order…"
                    : paymentOption === "pay_after_work"
                      ? `Confirm Booking · Pay ${fmt(total)} After Work`
                      : `Pay ${fmt(total)} & Confirm`}
                </button>
              </div>
            </section>
          </div>

          {/* ── RIGHT: Summary sidebar ── */}
          <div style={S.rightColumn}>
            <section style={S.summaryCard}>
              {loggedInUser && (
                <div style={S.profileCard}>
                  <div style={S.profileAvatar}>{userName.charAt(0).toUpperCase()}</div>
                  <div>
                    <div style={S.profileLabel}>Booking for</div>
                    <div style={S.profileName}>{userName}</div>
                    {loggedInUser.mobile && <div style={S.profileMeta}>{loggedInUser.mobile}</div>}
                  </div>
                </div>
              )}
              <div style={S.summaryHead}>
                <div>
                  <h3 style={S.summaryTitle}>Booking Summary</h3>
                  <p style={S.summaryMeta}>{cartItems.length} services · {totalQty} items</p>
                  {paymentOption === "pay_after_work"
                    ? <div style={S.splitPill}>⏳ Pay after work is done</div>
                    : <div style={S.payNowPill}>💳 Pay at checkout</div>
                  }
                  {canPayLater && (
                    <div style={{ marginTop:"6px", fontSize:"11px", color:"#92400e", fontWeight:600 }}>
                      Total &gt; ₹{(CONFIG.PARTIAL_PAYMENT_THRESHOLD ?? 1000).toLocaleString("en-IN")} · Pay later available
                    </div>
                  )}
                </div>
                <button style={S.ghostBtnSmall}
                  onClick={() => navigate("/profile-dashboard", { state:{ openCart:true } })}>
                  Edit Cart
                </button>
              </div>
              <div style={S.summaryItems}>
                {cartItems.map((item, i) => (
                  <div key={item.id||i} style={S.summaryItem}>
                    {item.image
                      ? <img src={item.image} alt={item.name} style={S.summaryImg} />
                      : <div style={S.summaryImgFallback}>{item.name?.charAt(0)||"S"}</div>
                    }
                    <div style={S.summaryItemBody}>
                      <div style={S.summaryItemTop}>
                        <h4 style={S.summaryItemName}>{item.name||item.title}</h4>
                        <div style={S.summaryItemPrice}>{fmt(Number(item.price||0)*Number(item.qty||1))}</div>
                      </div>
                      <div style={S.summaryItemDesc}>{item.short||item.subtitle||"Professional service"}</div>
                      <div style={{ ...S.slotPillMini, ...(isShoppingItem(item)||(item.bookingDate&&item.bookingTime)?S.slotPillActive:S.slotPillPending), marginTop:6, display:"inline-flex" }}>
                        {isShoppingItem(item) ? "📦 No scheduling needed" : formatSlotLabel(item)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={S.billWrap}>
                <div style={S.billRow}><span>Base Fare</span><span>{fmt(subtotal)}</span></div>
                <div style={S.billRow}><span>Discount ({CHARGES.DISCOUNT_PERCENT}%)</span><span style={S.positiveValue}>−{fmt(discount)}</span></div>
                <div style={S.billRow}><span>Tax ({effectiveTaxPercent}%)</span><span>{fmt(gst)}</span></div>
                <div style={S.billRow}><span>Platform Fee</span><span>{fmt(platformFee)}</span></div>
                {tipAmount > 0 && (
                  <div style={S.billRow}><span>Partner Tip</span><span>{fmt(tipAmount)}</span></div>
                )}
              </div>
              <div style={S.totalRow}><span>Total Amount</span><span>{fmt(total)}</span></div>
              <div style={S.savingBox}>🎉 You save {fmt(discount)} on this booking</div>
            </section>
          </div>

        </div>
      </div>

      <SlotModal open={slotModalOpen} item={selectedItem}
        availabilityDays={availabilityDays} loadingAvailability={loadingAvailability}
        availabilityError={availabilityError} selectedAvailabilityDate={selectedAvailabilityDate}
        onSelectDate={handleSelectAvailabilityDate} draftDate={draftDate} draftTime={draftTime}
        onSelectTime={handleSelectTime} onClose={resetModalState} onSave={handleSaveSlot} />
    </div>
  );
};

const SectionTitle = ({ title, desc, right }) => (
  <div style={S.sectionHead}>
    <div><h3 style={S.cardTitle}>{title}</h3>{desc&&<p style={S.cardDesc}>{desc}</p>}</div>
    {right}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────────────────────
const S = {
  page:              { background:"#f1f5f9", minHeight:"100vh", padding:"28px", fontFamily:"Inter,Arial,sans-serif", color:"#0f172a" },
  container:         { maxWidth:"1440px", margin:"0 auto" },
  topHero:           { display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:"24px", marginBottom:"24px", flexWrap:"wrap" },
  eyebrow:           { margin:0, color:"#4f46e5", fontSize:"13px", fontWeight:800, textTransform:"uppercase", letterSpacing:"0.08em" },
  pageTitle:         { margin:"8px 0 0", fontSize:"34px", fontWeight:900, color:"#0f172a" },
  pageSubtitle:      { margin:"10px 0 0", fontSize:"15px", color:"#64748b", lineHeight:1.7 },
  payableCard:       { minWidth:"200px", background:"#fff", border:"1px solid #e2e8f0", borderRadius:"20px", padding:"18px 20px" },
  paymentOptionBox:   { marginTop:"12px", padding:"10px 12px", background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:"12px", display:"flex", flexDirection:"column", gap:"8px" },
  paymentOptionTitle: { fontSize:"11px", fontWeight:700, color:"#6b7280", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:"2px" },
  paymentOptionRow:   { display:"flex", alignItems:"flex-start", gap:"8px", fontSize:"13px", color:"#1e293b", cursor:"pointer", lineHeight:1.4 },
  paymentOptionSub:   { color:"#64748b", fontWeight:400 },
  splitPill:          { display:"inline-block", marginTop:"6px", padding:"3px 10px", borderRadius:"999px", background:"#fff7ed", border:"1px solid #fed7aa", color:"#c2410c", fontSize:"12px", fontWeight:700 },
  payLaterBadge: { marginTop:"10px", padding:"7px 12px", borderRadius:"10px", background:"#fff7ed", border:"1px solid #fdba74", color:"#c2410c", fontSize:"12px", fontWeight:700, textAlign:"center" },
  payNowBadge:   { marginTop:"10px", padding:"7px 12px", borderRadius:"10px", background:"#ecfdf5", border:"1px solid #bbf7d0", color:"#15803d", fontSize:"12px", fontWeight:700, textAlign:"center" },
  payNowPill:    { display:"inline-block", marginTop:"6px", padding:"3px 10px", borderRadius:"999px", background:"#ecfdf5", border:"1px solid #bbf7d0", color:"#15803d", fontSize:"12px", fontWeight:700 },
  payableLabel:  { fontSize:"13px", color:"#64748b", fontWeight:700 },
  payableValue:      { marginTop:"6px", fontSize:"32px", fontWeight:900, color:"#312e81" },
  payableMeta:       { marginTop:"4px", fontSize:"13px", color:"#64748b" },
  stepWrap:          { display:"grid", gridTemplateColumns:"repeat(4,minmax(0,1fr))", gap:"14px", marginBottom:"24px" },
  stepItem:          { display:"flex", alignItems:"center", gap:"12px", background:"#fff", border:"1px solid #e2e8f0", borderRadius:"16px", padding:"14px", minHeight:"72px" },
  stepItemActive:    { background:"#eef2ff", border:"1px solid #c7d2fe" },
  stepCircle:        { width:"36px", height:"36px", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", background:"#f8fafc", border:"1px solid #e2e8f0", color:"#64748b", fontSize:"13px", fontWeight:900, flexShrink:0 },
  stepCircleDone:    { background:"#ecfdf5", border:"1px solid #bbf7d0", color:"#16a34a" },
  stepCircleActive:  { background:"#4338ca", border:"none", color:"#fff" },
  stepInfo:          { display:"flex", flexDirection:"column", gap:"2px" },
  stepTitle:         { fontSize:"14px", fontWeight:800 },
  stepSub:           { fontSize:"12px", color:"#94a3b8" },
  mainGrid:          { display:"grid", gridTemplateColumns:"minmax(0,1.75fr) minmax(340px,0.95fr)", gap:"24px", alignItems:"start" },
  leftColumn:        { display:"flex", flexDirection:"column", gap:"20px", minWidth:0 },
  rightColumn:       { display:"flex", flexDirection:"column", gap:"18px", minWidth:0 },
  card:              { background:"#fff", border:"1px solid #e2e8f0", borderRadius:"22px", padding:"22px" },
  sectionHead:       { display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:"16px", marginBottom:"16px", flexWrap:"wrap" },
  cardTitle:         { margin:0, fontSize:"22px", fontWeight:900 },
  cardDesc:          { margin:"6px 0 0", fontSize:"14px", color:"#64748b", lineHeight:1.6 },
  ghostBtn:          { height:"40px", padding:"0 14px", borderRadius:"10px", border:"1px solid #e2e8f0", background:"#fff", color:"#334155", cursor:"pointer", fontWeight:700 },
  ghostBtnSmall:     { height:"36px", padding:"0 12px", borderRadius:"10px", border:"1px solid #e2e8f0", background:"#fff", color:"#334155", cursor:"pointer", fontWeight:700, whiteSpace:"nowrap" },
  safeTag:           { padding:"6px 12px", borderRadius:"999px", background:"#ecfdf5", border:"1px solid #bbf7d0", color:"#15803d", fontSize:"12px", fontWeight:800 },
  addressLayout:     { display:"grid", gridTemplateColumns:"minmax(260px,0.9fr) minmax(0,1.1fr)", gap:"16px" },
  addressCard:         { border:"1px solid #e2e8f0", borderRadius:"16px", padding:"16px", background:"#fbfdff", display:"flex", flexDirection:"column", gap:"12px", transition:"border 0.15s" },
  addressCardSelected: { border:"2px solid #4f46e5", background:"#fafbff" },
  addrBadge:           { width:"fit-content", padding:"5px 10px", borderRadius:"999px", background:"#eef2ff", color:"#4338ca", fontSize:"13px", fontWeight:800 },
  addressBlock:        { fontSize:"14px", color:"#334155", lineHeight:1.8 },
  outlineBtn:          { width:"fit-content", height:"42px", padding:"0 14px", borderRadius:"10px", border:"1px solid #c7d2fe", background:"#fff", color:"#4f46e5", cursor:"pointer", fontWeight:800 },
  addrStateBox:        { borderRadius:"14px", border:"1px solid #e2e8f0", background:"#f8fafc", padding:"28px 20px", textAlign:"center", fontSize:"14px", color:"#475569" },
  addrTabRow:        { display:"flex", gap:"8px", marginBottom:"16px" },
  addrTab:           { height:"38px", padding:"0 16px", borderRadius:"10px", border:"1px solid #e2e8f0", background:"#fff", color:"#475569", cursor:"pointer", fontWeight:700, fontSize:"13px" },
  addrTabActive:     { border:"1px solid #4f46e5", background:"#eef2ff", color:"#4338ca" },
  testingBadge:      { background:"#fffbeb", border:"1px solid #fde68a", color:"#92400e", fontSize:"12.5px", lineHeight:1.6, borderRadius:"12px", padding:"12px 14px", marginBottom:"14px" },
  detectBtn:         { height:"44px", padding:"0 18px", borderRadius:"12px", border:"1.5px solid #4f46e5", background:"#eef2ff", color:"#4338ca", fontWeight:800, fontSize:"13.5px", cursor:"pointer" },
  locationErrorText: { fontSize:"12.5px", color:"#dc2626", marginTop:"10px" },
  testSelect:        { width:"100%", maxWidth:"360px", height:"44px", padding:"0 14px", borderRadius:"12px", border:"1.5px solid #e2e8f0", background:"#fff", color:"#111827", fontSize:"13.5px" },
  mapCard:           { border:"1px solid #e2e8f0", borderRadius:"16px", overflow:"hidden", minHeight:"200px", background:"#f8fafc" },
  mapImage:          { width:"100%", height:"100%", objectFit:"cover", display:"block" },
  serviceSlotList:   { display:"flex", flexDirection:"column", gap:"12px" },
  serviceSlotCard:   { border:"1px solid #e2e8f0", borderRadius:"16px", padding:"14px", background:"#fbfdff", display:"flex", justifyContent:"space-between", gap:"14px", alignItems:"center", flexWrap:"wrap" },
  serviceSlotLeft:   { display:"flex", gap:"12px", alignItems:"flex-start", flex:1, minWidth:0 },
  summaryImg:        { width:"70px", height:"70px", borderRadius:"12px", objectFit:"cover", flexShrink:0 },
  summaryImgFallback:{ width:"70px", height:"70px", borderRadius:"12px", background:"linear-gradient(135deg,#4f46e5,#4338ca)", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"22px", fontWeight:900, flexShrink:0 },
  summaryItemTop:    { display:"flex", justifyContent:"space-between", gap:"10px", alignItems:"flex-start" },
  summaryItemName:   { margin:0, fontSize:"15px", fontWeight:800 },
  summaryItemPrice:  { fontSize:"15px", fontWeight:800, whiteSpace:"nowrap" },
  summaryItemDesc:   { marginTop:"4px", fontSize:"13px", color:"#64748b" },
  slotPill:          { display:"inline-flex", alignItems:"center", padding:"7px 12px", borderRadius:"999px", fontSize:"13px", fontWeight:700 },
  slotPillMini:      { padding:"5px 10px", borderRadius:"999px", fontSize:"12px", fontWeight:700 },
  slotPillActive:    { background:"#ecfdf5", border:"1px solid #bbf7d0", color:"#15803d" },
  slotPillPending:   { background:"#fff7ed", border:"1px solid #fdba74", color:"#c2410c" },
  slotEditBtn:       { height:"42px", padding:"0 14px", borderRadius:"10px", border:"1px solid #c7d2fe", background:"#fff", color:"#4f46e5", cursor:"pointer", fontWeight:800, whiteSpace:"nowrap" },
  deleteIconBtn:     { width:"42px", height:"42px", borderRadius:"10px", border:"1px solid #fecaca", background:"#fff1f2", color:"#dc2626", cursor:"pointer", fontSize:"16px", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
  slotStatusOk:      { padding:"6px 12px", borderRadius:"999px", background:"#ecfdf5", border:"1px solid #bbf7d0", color:"#15803d", fontSize:"12px", fontWeight:800 },
  slotStatusPending: { padding:"6px 12px", borderRadius:"999px", background:"#fff7ed", border:"1px solid #fdba74", color:"#c2410c", fontSize:"12px", fontWeight:800 },
  textarea:          { width:"100%", minHeight:"110px", borderRadius:"14px", border:"1px solid #e2e8f0", background:"#fff", padding:"12px 14px", fontSize:"14px", outline:"none", resize:"vertical", boxSizing:"border-box", color:"#0f172a", lineHeight:1.6 },
  footerActionCard:  { background:"#fff", border:"1px solid #e2e8f0", borderRadius:"20px", padding:"18px 20px", display:"flex", flexDirection:"column", gap:"14px" },
  errorBanner:       { background:"#fef2f2", border:"1px solid #fecaca", color:"#991b1b", padding:"10px 14px", borderRadius:"10px", fontWeight:600, fontSize:"14px" },
  warnBanner:        { background:"#fff7ed", border:"1px solid #fdba74", color:"#c2410c", padding:"10px 14px", borderRadius:"10px", fontWeight:600, fontSize:"14px" },
  disabledServiceBanner: { background:"#fef2f2", border:"1px solid #fca5a5", color:"#991b1b", padding:"12px 14px", borderRadius:"10px", fontWeight:600, fontSize:"14px", marginBottom:"6px" },
  termsWrap:         { display:"flex", alignItems:"flex-start", gap:"10px", fontSize:"14px", color:"#475569", lineHeight:1.6 },
  inlineLink:        { color:"#4f46e5", textDecoration:"none", fontWeight:800 },
  footerBtns:        { display:"flex", gap:"12px", justifyContent:"flex-end" },
  secondaryBtn:      { height:"48px", padding:"0 20px", borderRadius:"10px", border:"1px solid #c7d2fe", background:"#fff", color:"#4f46e5", fontWeight:800, cursor:"pointer" },
  primaryBtn:        { height:"48px", padding:"0 24px", borderRadius:"10px", border:"none", background:"linear-gradient(135deg,#4f46e5,#4338ca)", color:"#fff", fontWeight:900, cursor:"pointer" },
  disabledBtn:       { background:"#c7d2fe", cursor:"not-allowed" },
  summaryCard:       { position:"sticky", top:"20px", background:"#fff", border:"1px solid #e2e8f0", borderRadius:"22px", padding:"20px" },
  profileCard:       { display:"flex", alignItems:"center", gap:"12px", padding:"12px 14px", borderRadius:"16px", background:"#eef2ff", marginBottom:"16px" },
  profileAvatar:     { width:"44px", height:"44px", borderRadius:"50%", background:"linear-gradient(135deg,#4f46e5,#4338ca)", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"18px", fontWeight:900, flexShrink:0 },
  profileLabel:      { fontSize:"12px", color:"#64748b" },
  profileName:       { fontSize:"15px", color:"#0f172a", fontWeight:800 },
  profileMeta:       { fontSize:"12px", color:"#64748b", marginTop:"2px" },
  summaryHead:       { display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:"10px", marginBottom:"14px" },
  summaryTitle:      { margin:0, fontSize:"22px", fontWeight:900 },
  summaryMeta:       { margin:"4px 0 0", fontSize:"14px", color:"#64748b" },
  summaryItems:      { display:"flex", flexDirection:"column", gap:"14px" },
  summaryItem:       { display:"flex", gap:"12px", alignItems:"flex-start", paddingBottom:"14px", borderBottom:"1px solid #f1f5f9" },
  summaryItemBody:   { flex:1, minWidth:0 },
  summaryItemBottom: { marginTop:"8px" },
  billWrap:          { marginTop:"16px", padding:"14px 0", borderTop:"1px solid #e2e8f0", borderBottom:"1px solid #e2e8f0", display:"grid", gap:"10px" },
  billRow:           { display:"flex", justifyContent:"space-between", fontSize:"14px", color:"#334155" },
  positiveValue:     { color:"#16a34a", fontWeight:800 },
  totalRow:          { display:"flex", justifyContent:"space-between", paddingTop:"14px", fontSize:"24px", fontWeight:900 },
  savingBox:         { marginTop:"12px", padding:"10px 12px", borderRadius:"12px", background:"#ecfdf5", border:"1px solid #bbf7d0", color:"#15803d", fontSize:"13px", fontWeight:800 },
  modalOverlay:      { position:"fixed", inset:0, background:"rgba(15,23,42,0.55)", display:"flex", alignItems:"center", justifyContent:"center", padding:"16px", zIndex:9999 },
  modalCard:         { width:"min(920px,100%)", maxHeight:"92vh", overflow:"hidden", background:"#fff", borderRadius:"22px", border:"1px solid #e2e8f0", boxShadow:"0 25px 60px rgba(15,23,42,0.2)", display:"flex", flexDirection:"column" },
  modalHead:         { display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:"16px", padding:"18px 20px 12px", borderBottom:"1px solid #eef2ff" },
  modalTitle:        { margin:0, fontSize:"24px", fontWeight:900 },
  modalDesc:         { margin:"6px 0 0", fontSize:"14px", color:"#64748b" },
  modalBody:         { padding:"16px 20px", overflowY:"auto" },
  modalCloseBtn:     { width:"38px", height:"38px", borderRadius:"10px", border:"1px solid #e2e8f0", background:"#fff", cursor:"pointer", fontSize:"14px", fontWeight:800, flexShrink:0 },
  modalServiceInfo:  { display:"flex", alignItems:"center", gap:"12px", background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:"14px", padding:"12px", marginBottom:"16px", flexWrap:"wrap" },
  modalServiceImg:   { width:"60px", height:"60px", borderRadius:"12px", objectFit:"cover" },
  modalServiceFallback:{ width:"60px", height:"60px", borderRadius:"12px", background:"linear-gradient(135deg,#4f46e5,#4338ca)", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"22px", fontWeight:900 },
  modalServiceName:  { fontSize:"15px", fontWeight:800 },
  modalServiceSub:   { marginTop:"3px", fontSize:"13px", color:"#64748b" },
  modalServiceHint:  { marginTop:"4px", fontSize:"12px", color:"#4338ca", fontWeight:700 },
  slotSection:       { marginTop:"14px" },
  label:             { display:"block", marginBottom:"8px", fontSize:"13px", fontWeight:800, color:"#334155" },
  dateGrid:          { display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:"10px" },
  dateBtn:           { minHeight:"60px", borderRadius:"12px", border:"1px solid #e2e8f0", background:"#fff", color:"#334155", cursor:"pointer", fontWeight:700, padding:"10px 12px", textAlign:"left", display:"flex", flexDirection:"column", justifyContent:"center", gap:"3px" },
  dateBtnActive:     { background:"#eef2ff", border:"1px solid #4f46e5", color:"#312e81" },
  dateBtnFull:       { opacity:0.5, cursor:"not-allowed" },
  dateBtnTop:        { fontSize:"13px", fontWeight:800 },
  dateBtnBottom:     { fontSize:"11px", color:"#64748b" },
  slotGridResponsive:{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:"10px" },
  slotBtn:           { minHeight:"96px", borderRadius:"12px", border:"1px solid #e2e8f0", background:"#fff", color:"#334155", cursor:"pointer", fontWeight:700, padding:"10px 12px", textAlign:"left" },
  slotBtnActive:     { background:"#eef2ff", border:"1px solid #4f46e5", color:"#312e81" },
  slotBtnDisabled:   { background:"#f8fafc", border:"1px solid #e2e8f0", color:"#94a3b8", cursor:"not-allowed", opacity:0.8 },
  slotBtnTime:       { fontSize:"13px", fontWeight:800 },
  slotBtnMeta:       { marginTop:"5px", fontSize:"11px" },
  seatBadge:         { display:"inline-block", padding:"2px 7px", borderRadius:"999px", background:"#ecfdf5", border:"1px solid #bbf7d0", color:"#15803d", fontSize:"11px", fontWeight:800 },
  seatBadgeFull:     { display:"inline-block", padding:"2px 7px", borderRadius:"999px", background:"#fff1f2", border:"1px solid #fecaca", color:"#dc2626", fontSize:"11px", fontWeight:800 },
  capacityBarWrap:   { marginTop:"7px", height:"4px", borderRadius:"999px", background:"#e2e8f0", overflow:"hidden" },
  capacityBar:       { height:"100%", borderRadius:"999px", transition:"width 0.3s" },
  slotCapacityText:  { marginTop:"3px", fontSize:"10px", color:"#94a3b8" },
  selectedSlotPreview:{ marginTop:"14px", borderRadius:"12px", border:"1px solid #dbeafe", background:"#f8fbff", padding:"12px 14px", display:"flex", flexDirection:"column", gap:"4px" },
  selectedSlotLabel: { fontSize:"11px", color:"#64748b", fontWeight:700 },
  selectedSlotValue: { fontSize:"14px", color:"#0f172a", fontWeight:800 },
  backendSampleCard: { marginTop:"14px", borderRadius:"12px", border:"1px dashed #c7d2fe", background:"#f8faff", padding:"12px 14px" },
  backendSampleTitle:{ fontSize:"12px", fontWeight:800, color:"#312e81", marginBottom:"6px" },
  backendSampleBody: { fontSize:"12px", color:"#334155", lineHeight:1.7 },
  availabilityStateBox:{ borderRadius:"12px", border:"1px solid #e2e8f0", background:"#f8fafc", padding:"16px", fontSize:"14px", color:"#475569", textAlign:"center" },
  availabilityErrorBox:{ borderRadius:"12px", border:"1px solid #fecaca", background:"#fff1f2", padding:"16px", fontSize:"14px", color:"#b91c1c", textAlign:"center" },
  modalFooter:       { display:"flex", justifyContent:"flex-end", gap:"10px", padding:"14px 20px 18px", borderTop:"1px solid #eef2ff", flexWrap:"wrap" },

  // ── Partner tip box (footer CTA area) ─────────────────────────────────────
  tipBox:             { background:"#fdf4ff", border:"1px solid #f0abfc", borderRadius:"16px", padding:"14px 16px" },
  tipTitle:           { fontSize:"14px", fontWeight:800, color:"#701a75" },
  tipSub:             { marginTop:"4px", fontSize:"12px", color:"#a21caf" },
  tipChipRow:         { marginTop:"12px", display:"flex", flexWrap:"wrap", alignItems:"flex-start", gap:"8px" },
  tipChip:            { flex:"0 0 auto", width:"auto", padding:"8px 16px", borderRadius:"999px", border:"1.5px solid #e9d5ff", background:"#fff", color:"#701a75", fontWeight:800, fontSize:"13px", cursor:"pointer", whiteSpace:"nowrap" },
  tipChipActive:      { border:"1.5px solid #a21caf", background:"#a21caf", color:"#fff" },
  tipRemoveBtn:        { flex:"0 0 auto", width:"auto", padding:"8px 14px", borderRadius:"999px", border:"1.5px solid #fecaca", background:"#fff1f2", color:"#dc2626", fontWeight:700, fontSize:"12px", cursor:"pointer", whiteSpace:"nowrap" },
  tipCustomRow:       { marginTop:"10px", display:"flex", gap:"8px" },
  tipCustomInput:     { flex:"1 1 140px", minWidth:"140px", height:"38px", borderRadius:"10px", border:"1px solid #e9d5ff", padding:"0 12px", fontSize:"13px", outline:"none", boxSizing:"border-box" },
  tipCustomApplyBtn:  { flex:"0 0 auto", width:"auto", height:"38px", padding:"0 20px", borderRadius:"10px", border:"none", background:"#a21caf", color:"#fff", fontWeight:800, fontSize:"13px", cursor:"pointer", whiteSpace:"nowrap" },
  tipMinHint:         { marginTop:"6px", fontSize:"11px", color:"#dc2626", fontWeight:600 },
  tipConfirm:         { marginTop:"10px", fontSize:"12px", color:"#701a75", fontWeight:700, background:"#fae8ff", border:"1px solid #f0abfc", borderRadius:"8px", padding:"6px 10px" },

  // ── Payment breakdown box (footer CTA area) ──────────────────────────────
  payBreakdownBox:    { background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:"16px", padding:"14px 16px" },
  payBreakdownTitle:  { fontSize:"12px", fontWeight:800, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:"10px" },
  payBreakdownRows:   { display:"flex", flexDirection:"column", gap:"8px" },
  payBreakdownRow:    { display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:"14px", color:"#334155" },
  payBreakdownTotal:  { borderTop:"1.5px solid #e2e8f0", paddingTop:"8px", marginTop:"4px", fontWeight:900, fontSize:"16px", color:"#0f172a" },
  saveNote:           { marginTop:"10px", fontSize:"12px", color:"#15803d", fontWeight:700, background:"#ecfdf5", border:"1px solid #bbf7d0", borderRadius:"8px", padding:"6px 10px" },

  // ── Coupon code ───────────────────────────────────────────────────────────
  couponInputRow:     { display:"flex", gap:"8px" },
  couponInput:        { flex:1, padding:"10px 12px", fontSize:"14px", border:"1px solid #cbd5e1", borderRadius:"10px", outline:"none" },
  couponApplyBtn:      { padding:"10px 18px", fontSize:"14px", fontWeight:700, color:"#fff", background:"#0f172a", border:"none", borderRadius:"10px", cursor:"pointer" },
  couponAppliedRow:   { display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:"13px" },
  couponRemoveBtn:    { padding:"4px 10px", fontSize:"12px", fontWeight:600, color:"#64748b", background:"transparent", border:"1px solid #cbd5e1", borderRadius:"8px", cursor:"pointer" },
  couponErrorText:    { marginTop:"8px", fontSize:"12px", color:"#dc2626", fontWeight:600 },

  // ── Payment option selector ───────────────────────────────────────────────
  payOptionWrap:      { background:"#fefce8", border:"1px solid #fde68a", borderRadius:"16px", padding:"14px 16px" },
  payOptionTitle:     { fontSize:"13px", fontWeight:800, color:"#92400e", marginBottom:"10px" },
  payOptionRow:       { display:"flex", gap:"10px", flexWrap:"wrap" },
  payOptionBtn:       { flex:1, minWidth:"160px", display:"flex", alignItems:"center", gap:"10px", padding:"12px 14px", borderRadius:"12px", border:"1.5px solid #e2e8f0", background:"#fff", cursor:"pointer", textAlign:"left", transition:"border 0.15s, background 0.15s" },
  payOptionBtnActive: { border:"2px solid #4f46e5", background:"#eef2ff" },
  payOptionIcon:      { fontSize:"22px", flexShrink:0 },
  payOptionTextWrap:  { flex:1, minWidth:0 },
  payOptionLabel:     { fontSize:"14px", fontWeight:800, color:"#0f172a" },
  payOptionSub:       { fontSize:"12px", color:"#64748b", marginTop:"2px" },
  payOptionCheck:     { width:"22px", height:"22px", borderRadius:"50%", background:"#4f46e5", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"12px", fontWeight:900, flexShrink:0 },
};

// ─────────────────────────────────────────────────────────────────────────────
// ROUTER WRAPPER — routes pay_order mode to PayOrderPage, otherwise full checkout
// Hooks called unconditionally here; early return is safe because no hooks follow it.
// ─────────────────────────────────────────────────────────────────────────────
const ScheduleSummaryPage = () => {
  const location     = useLocation();
  const navigate     = useNavigate();
  const loggedInUser = useMemo(() => getLoggedInUser(), []);
  const userMobile   = loggedInUser?.mobile || loggedInUser?.phone || location.state?.user?.mobile || "";

  if (location.state?.mode === "pay_order") {
    return (
      <PayOrderPage
        order={location.state.existingOrder}
        userMobile={userMobile}
        navigate={navigate}
        tipOnly={location.state?.tipOnly === true}
        initialTip={location.state?.initialTip || 0}
      />
    );
  }
  if (location.state?.mode === "confirm_tip") {
    return (
      <ConfirmDonePage
        order={location.state.existingOrder}
        userMobile={userMobile}
        navigate={navigate}
      />
    );
  }
  if (location.state?.mode === "reschedule") {
    return (
      <ReschedulePage
        order={location.state.existingOrder}
        userMobile={userMobile}
        navigate={navigate}
      />
    );
  }
  return <SummaryPageContent />;
};

export default ScheduleSummaryPage;